import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Building2, CalendarClock, CheckCircle2, ChevronDown, Pencil, Search, Send, Trash2, UserRound, X, XCircle } from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { createOriginalChangeCase } from '@/services/originalChangeCaseService'
import type { StudioClosureType } from '@/services/studioStateChangeService'
import { listMcUsersForTuAdmin } from '@/services/territorialManagerService'
import type { McUserForTuAdmin } from '@/types'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store'

type StudioOption = {
  studio_internal_id: number
  studio_number: string
  city_name: string | null
  address: string | null
  status_code: string | null
  partner_number: number | null
  persons_fio: string | null
  suggested_case_manager_name: string | null
  ownership_started_on: string | null
  other_studios_summary: string | null
  current_series_id: number | null
  current_contract_id: number | null
  contract_number: string | null
  contract_legal_status_label: string | null
  contract_actual_end_date: string | null
  royalty_debt_rub: number | null
  active_clients_count: number | null
  active_clients_snapshot_on: string | null
}

type ClosurePreparationRow = {
  id: number
  studio_internal_id: number
  studio_number: string
  city_name: string | null
  address: string | null
  status_code: string | null
  partner_number: number | null
  persons_fio: string | null
  ownership_started_on: string | null
  other_studios_summary: string | null
  current_series_id: number | null
  current_contract_id: number | null
  contract_number: string | null
  contract_legal_status_label: string | null
  contract_actual_end_date: string | null
  royalty_debt_rub: number | null
  active_clients_count: number | null
  active_clients_snapshot_on: string | null
  preparation_started_on: string
  expected_closure_on: string | null
  owner_official_notice_received: boolean
  owner_official_notice_received_on: string | null
  closure_confirmation_received: boolean
  closure_confirmation_received_on: string | null
  termination_signed: boolean
  termination_signed_on: string | null
  note: string | null
  current_stage: ClosureFormStep
  case_kind: ClosureCaseKind
  signal_source: string | null
  case_manager_user_id: string | null
  case_manager_name: string | null
  partner_contact_status: PartnerContactStatus | null
  closure_reason: string | null
  negotiation_summary: string | null
  management_decision: ManagementDecision | null
  management_decision_on: string | null
  management_decision_note: string | null
  management_online_help_decision: OnlineHelpDecision | null
  management_online_help_until: string | null
  management_severance_terms: string | null
  management_save_attempt_required: boolean
  partner_message_sent_on: string | null
  partner_message_channel: string | null
  partner_message_address: string | null
  partner_response_due_on: string | null
  partner_message_sent_confirmed: boolean
  partner_message_received_confirmed: boolean
  partner_notice_missing_reason: string | null
  client_liability_amount: number | null
  client_liability_count: number | null
  client_liability_snapshot_on: string | null
  client_liability_note: string | null
  warning_passwords_changed: boolean
  warning_chats_removed: boolean
  warning_socials_removed: boolean
  warning_access_lost: boolean
  warning_partner_unreachable: boolean
  warning_note: string | null
  legal_block_status: WorkBlockStatus
  legal_block_due_on: string | null
  legal_block_assignee: string | null
  legal_block_note: string | null
  legal_regulation_sent_on: string | null
  legal_partner_response_due_on: string | null
  legal_documents_due_on: string | null
  legal_reminder_sent_on: string | null
  legal_termination_path: TerminationPath
  finance_block_status: WorkBlockStatus
  finance_block_due_on: string | null
  finance_block_assignee: string | null
  finance_block_note: string | null
  finance_contracts_checked: boolean
  finance_revenue_checked: boolean
  finance_royalty_accrued: boolean
  finance_debt_fixed: boolean
  finance_late_payments_wait_until: string | null
  tech_block_status: WorkBlockStatus
  tech_block_due_on: string | null
  tech_block_assignee: string | null
  tech_block_note: string | null
  tech_liability_precheck_done: boolean
  tech_systems_transfer_ready: boolean
  tech_listok_hide_requested: boolean
  tech_accesses_closed_on: string | null
  pedagogy_block_status: WorkBlockStatus
  pedagogy_block_due_on: string | null
  pedagogy_block_assignee: string | null
  pedagogy_block_note: string | null
  pedagogy_secret_shoppers_stopped: boolean
  pedagogy_plans_adjusted: boolean
  pedagogy_accesses_closed: boolean
  sales_block_status: WorkBlockStatus
  sales_block_due_on: string | null
  sales_block_assignee: string | null
  sales_block_note: string | null
  sales_price_amount: number | null
  sales_has_buyer: boolean
  sales_buyer_summary: string | null
  sales_transfer_due_on: string | null
  sales_openings_handoff_done: boolean
  sales_buyer_dkk_synced: boolean
  preparation_ended_on: string | null
  end_kind: 'COMPLETED' | 'CANCELLED' | null
  end_reason: string | null
  ended_by_name: string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
}

type ClosureCaseKind = 'NETWORK_EXIT' | 'SINGLE_STUDIO_CLOSURE' | 'STUDIO_SALE' | 'OTHER'
type PartnerContactStatus = 'CONTACTABLE' | 'UNSTABLE' | 'UNREACHABLE' | 'CONFLICT'
type ManagementDecision =
  | 'CLOSURE_APPROVED'
  | 'CLOSURE_WITH_TERMS'
  | 'UNILATERAL_TERMINATION'
  | 'TRY_SAVE_STUDIO'
  | 'MOVE_TO_SALE'
  | 'ONLINE_HELP_APPROVED'
  | 'ONLINE_HELP_DECLINED'
  | 'NEEDS_REVIEW'
type WorkBlockStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'WAITING_PARTNER' | 'DONE' | 'NOT_REQUIRED' | 'OVERDUE'
type OnlineHelpDecision = 'APPROVED' | 'DECLINED' | 'NOT_REQUIRED' | 'NEEDS_REVIEW'
type TerminationPath = 'WAITING' | 'BILATERAL' | 'UNILATERAL' | 'NOT_REQUIRED'
type CaseScenarioStatus = 'CLOSURE_CONFIRMED' | 'STUDIO_CONTINUES' | 'REBRANDING' | 'OTHER_FORMAT' | 'MANAGEMENT_REVIEW'
type StatusHandoffTarget = 'NO_SALES' | 'CLOSED'
type RegulationDetails = Record<string, unknown>
const TECH_CLOSURE_ITEMS = [
  { id: 'listok_blocked', label: 'Блокировка Листка' },
  { id: 'amo_users_deactivated', label: 'Деактивация пользователей в АМО' },
  { id: 'client_chat_members_removed', label: 'Удаление из чата клиентов Тг/Макс + сотрудники франчайзи' },
  { id: 'messages_access_closed', label: 'Закрываем доступ к сообщениям для всех участников' },
  { id: 'partner_chats_removed', label: 'Удаление из чатов и каналов партнеров' },
  { id: 'google_mail_password_changed', label: 'Смена пароля на почте гугл + удалить доступ к папке ГК' },
  { id: 'yandex_mail_password_changed', label: 'Смена пароля на почте яндекс' },
  { id: 'vk_admin_removed', label: 'Удаление админских прав в ВК' },
  { id: 'ok_admin_removed', label: 'Удаление админских прав в ОК' },
  { id: 'livedune_access_removed', label: 'Удалить доступ в лайфдюн' },
  { id: 'instagram_password_changed', label: 'Смена пароля в инст, выйти со всех устройств' },
  { id: 'gc_franchisee_blocked', label: 'Блок на ГК франчайзи, удалить с ГК сотрудников' },
  { id: 'maps_access_closed', label: 'Закрытие доступа на картах' },
  { id: 'rao_vois_notice_sent', label: 'Направить уведомление о расторжении РАО и ВОИс' },
  { id: 'gc_academy_blocked', label: 'Блокировка на ГК академий' },
  { id: 'uk_app_disabled', label: 'Отключить от приложения Управляйки' },
  { id: 'pointer_services_disabled', label: 'Отключить сервисы (поинтер)' },
  { id: 'banks_discounts_disabled', label: 'Отключить от банков и скидок' },
] as const
const countTechClosureItems = (checklist: Record<string, boolean>) =>
  TECH_CLOSURE_ITEMS.filter(item => checklist[item.id]).length

type ClosureFormStep = 'signal' | 'decision' | 'partner' | 'departments' | 'legal' | 'coordination' | 'finance' | 'termination' | 'technical'
type ClosureCaseMode = 'create' | 'manage'
type DepartmentBlockId = 'legal' | 'finance' | 'tech' | 'pedagogy' | 'sales'

type CaseManagerOption = Pick<McUserForTuAdmin, 'user_id' | 'manager_name' | 'tg_username' | 'email' | 'job_title'>
type CaseManagerSuggestion = {
  studio_number: string
  case_manager_user_id: string
  case_manager_name: string | null
}

function isCaseManagerCandidate(manager: CaseManagerOption) {
  if (manager.job_title === 'territorial_manager') return true
  const name = (manager.manager_name ?? '').trim().toLocaleLowerCase()
  return name === 'роман иванов' || name === 'татьяна копотун'
}

function caseManagerLabel(manager: CaseManagerOption): string {
  return manager.manager_name?.trim() || manager.tg_username || manager.email || manager.user_id
}

const CASE_KIND_LABELS: Record<ClosureCaseKind, string> = {
  NETWORK_EXIT: 'Выход из сети',
  SINGLE_STUDIO_CLOSURE: 'Закрытие студии',
  STUDIO_SALE: 'Продажа студии',
  OTHER: 'Другое',
}

const CONTACT_STATUS_LABELS: Record<PartnerContactStatus, string> = {
  CONTACTABLE: 'На связи',
  UNSTABLE: 'Отвечает нестабильно',
  UNREACHABLE: 'Не выходит на связь',
  CONFLICT: 'Спор/конфликт',
}

const DECISION_LABELS: Record<ManagementDecision, string> = {
  CLOSURE_APPROVED: 'Мировое закрытие',
  CLOSURE_WITH_TERMS: 'Закрытие с отступными',
  UNILATERAL_TERMINATION: 'Закрытие с односторонкой / отзывом бренда',
  TRY_SAVE_STUDIO: 'Пробуем сохранить',
  MOVE_TO_SALE: 'Пробуем продать',
  ONLINE_HELP_APPROVED: 'Онлайн-помощь даём',
  ONLINE_HELP_DECLINED: 'Онлайн-помощь не даём',
  NEEDS_REVIEW: 'Нужен разбор',
}

function buildClosureHandoffSummary(
  row: ClosurePreparationRow,
  target: StatusHandoffTarget,
  closureOn: string,
  closureType: StudioClosureType,
): string {
  const typeLabels: Record<StudioClosureType, string> = {
    STANDARD: 'Обычное закрытие',
    REBRANDING: 'Ребрендинг',
    UNAPPROVED: 'Не согласовано',
  }
  const isNoSales = target === 'NO_SALES'
  const lines = [
    isNoSales
      ? `Студия #${row.studio_number} не ведёт продажи; требуется установить статус «Без продаж».`
      : `Студия #${row.studio_number} фактически закрыта ${closureOn || 'в дату, указанную при передаче'}. Тип закрытия: ${typeLabels[closureType]}.`,
    row.closure_reason?.trim() ? `Причина по кейсу: ${row.closure_reason.trim()}` : null,
    row.management_decision
      ? `Решение УК: ${DECISION_LABELS[row.management_decision] ?? row.management_decision}${row.management_decision_note?.trim() ? ` — ${row.management_decision_note.trim()}` : ''}`
      : row.management_decision_note?.trim() ? `Комментарий к решению УК: ${row.management_decision_note.trim()}` : null,
    row.negotiation_summary?.trim() ? `По итогам переговоров: ${row.negotiation_summary.trim()}` : null,
  ].filter((line): line is string => Boolean(line))

  return `Коротко о закрытии:\n${lines.join('\n')}`
}

const BLOCK_STATUS_LABELS: Record<WorkBlockStatus, string> = {
  NOT_STARTED: 'Не начато',
  IN_PROGRESS: 'В работе',
  WAITING_PARTNER: 'Ждём франчайзи',
  DONE: 'Готово',
  NOT_REQUIRED: 'Не требуется',
  OVERDUE: 'Просрочено',
}

const ONLINE_HELP_LABELS: Record<OnlineHelpDecision, string> = {
  APPROVED: 'Онлайн-помощь даём',
  DECLINED: 'Онлайн-помощь не даём',
  NOT_REQUIRED: 'Не требуется',
  NEEDS_REVIEW: 'Нужно решить',
}

const TERMINATION_PATH_LABELS: Record<TerminationPath, string> = {
  WAITING: 'Путь не выбран',
  BILATERAL: 'Двухстороннее',
  UNILATERAL: 'Одностороннее',
  NOT_REQUIRED: 'Не требуется',
}

const CASE_KIND_OPTIONS = Object.entries(CASE_KIND_LABELS) as Array<[ClosureCaseKind, string]>
const CONTACT_STATUS_OPTIONS = Object.entries(CONTACT_STATUS_LABELS) as Array<[PartnerContactStatus, string]>
const DECISION_OPTIONS = ([
  'CLOSURE_APPROVED',
  'CLOSURE_WITH_TERMS',
  'UNILATERAL_TERMINATION',
  'TRY_SAVE_STUDIO',
  'MOVE_TO_SALE',
] as ManagementDecision[]).map(value => [value, DECISION_LABELS[value]] as [ManagementDecision, string])
const BLOCK_STATUS_OPTIONS = Object.entries(BLOCK_STATUS_LABELS) as Array<[WorkBlockStatus, string]>
const ONLINE_HELP_OPTIONS = Object.entries(ONLINE_HELP_LABELS) as Array<[OnlineHelpDecision, string]>
const TERMINATION_PATH_OPTIONS = Object.entries(TERMINATION_PATH_LABELS) as Array<[TerminationPath, string]>
const CASE_SCENARIO_STATUS_LABELS: Record<CaseScenarioStatus, string> = {
  CLOSURE_CONFIRMED: 'Закрытие подтверждается',
  STUDIO_CONTINUES: 'Студия продолжает работу',
  REBRANDING: 'Планируется ребрендинг',
  OTHER_FORMAT: 'Планируется другой формат',
  MANAGEMENT_REVIEW: 'Требуется повторное решение руководителя',
}

const LEGACY_CLOSURE_FORM_STEPS: Array<{ id: ClosureFormStep; title: string; description: string }> = [
  { id: 'signal', title: '1. Сигнал', description: 'Студия, тип кейса и вводные от УК' },
  { id: 'decision', title: '2. Решение', description: 'Сроки и позиция руководителя' },
  { id: 'partner', title: '3. Франчайзи', description: 'Отправка, уведомление и кредиторка' },
  { id: 'departments', title: '4. Отделы', description: 'Юристы, бух, техотдел, педагоги и продажи' },
]

const CLOSURE_FORM_STEPS: Array<{ id: ClosureFormStep; title: string; description: string }> = [
  { id: 'signal', title: '0. Новые кейсы', description: 'Назначение менеджера и сбор данных' },
  { id: 'decision', title: '1. Решение', description: 'Закрытие и отдельное решение по помощи' },
  { id: 'partner', title: '2. Отправка решения от УК', description: 'Условия, дата и действия франчайзи' },
  { id: 'legal', title: '3. Юридический запуск', description: 'Официальное уведомление и статус подготовки' },
  { id: 'finance', title: '4. Финальная сверка', description: 'Напоминание, финансы и срок 7 дней' },
  { id: 'termination', title: '5. Расторжение', description: 'Двусторонний или односторонний путь' },
  { id: 'technical', title: '6. Закрытие и база', description: 'Системы, соцсети и передача информации' },
]

const STAGE_TABLE_TITLES: Record<string, string> = {
  signal: 'Новые кейсы',
  decision: 'Кейсы на этапе решения',
  partner: 'Кейсы на этапе франчайзи',
  departments: 'Кейсы на этапе отделов',
  legal: 'Кейсы на этапе юридического запуска',
  coordination: 'Кейсы на этапе информирования подразделений',
  finance: 'Кейсы на этапе финальной сверки',
  termination: 'Кейсы на этапе расторжения',
  technical: 'Кейсы на этапе технического закрытия и передачи в базу',
}

const STAGE_TABLE_DESCRIPTIONS: Record<string, string> = {
  signal: 'Назначение менеджера и сбор данных',
  decision: 'По каждой строке видно решение руководителя, срок закрытия и условия.',
  partner: 'По каждой строке видно отправку франчайзи, уведомление и клиентскую кредиторку.',
  departments: 'По каждой строке видно задачи юристов, бухгалтерии, техотдела, педагогов и продаж.',
  legal: 'Юридические действия после получения официального уведомления.',
  coordination: 'Общее информирование подразделений и педагогический контроль до даты закрытия.',
  finance: 'Финальное напоминание, финансовые показатели и контроль срока ответа.',
  termination: 'Проверка получения информации и выбор пути расторжения.',
  technical: 'Действия после подписания или одностороннего расторжения и передача результата в базу.',
}

const DEPARTMENT_BLOCKS: Array<{ id: DepartmentBlockId; title: string }> = [
  { id: 'legal', title: 'Юристы' },
  { id: 'finance', title: 'Бухгалтерия' },
  { id: 'tech', title: 'Техотдел' },
  { id: 'pedagogy', title: 'Педагоги/Академия' },
  { id: 'sales', title: 'Продажи/открытия' },
]

function todayIso() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}

function daysUntil(date: string | null) {
  if (!date) return null
  const today = new Date(`${todayIso()}T00:00:00`)
  const target = new Date(`${date}T00:00:00`)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

function addCalendarDays(date: string | null, days: number) {
  if (!date) return null
  const value = new Date(`${date}T00:00:00`)
  value.setDate(value.getDate() + days)
  return value.toISOString().slice(0, 10)
}

function DeadlineSummary({ label, dueOn, completed = false, pendingText = 'Срок появится после исходного действия' }: { label: string; dueOn: string | null; completed?: boolean; pendingText?: string | null }) {
  const remaining = daysUntil(dueOn)
  const tone = completed
    ? 'border-success/40 bg-success/10 text-success'
    : remaining !== null && remaining < 0
      ? 'border-error/40 bg-error/10 text-error'
      : remaining !== null && remaining <= 3
        ? 'border-primary/40 bg-primary/10 text-primary'
        : 'border-border bg-muted/30 text-muted-foreground'
  const status = completed
    ? 'Выполнено'
    : !dueOn
      ? pendingText
      : remaining! < 0
        ? `Просрочено на ${Math.abs(remaining!)} дн.`
        : remaining === 0
          ? 'Срок сегодня'
          : `Осталось ${remaining} дн.`
  return <div className={`rounded-md border px-3 py-2 text-xs ${tone}`}><div className="font-medium">{label}</div>{(dueOn || status) && <div className="mt-0.5">{dueOn || '—'}{status ? ` · ${status}` : ''}</div>}</div>
}

function optionalNumber(value: string) {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : NaN
}

function blockDueOverdue(status: WorkBlockStatus, dueOn: string | null) {
  if (!dueOn || status === 'DONE' || status === 'NOT_REQUIRED') return false
  return daysUntil(dueOn) !== null && daysUntil(dueOn)! < 0
}

function formatOwnerLabel(row: Pick<StudioOption, 'partner_number' | 'persons_fio'>) {
  if (row.persons_fio?.trim()) {
    return row.partner_number ? `${row.persons_fio.trim()} (${row.partner_number})` : row.persons_fio.trim()
  }
  return row.partner_number ? `Франчайзи ${row.partner_number}` : ''
}

function formatStudioLabel(studio: Pick<StudioOption, 'studio_number' | 'city_name' | 'address' | 'partner_number' | 'persons_fio'>) {
  return [
    `#${studio.studio_number}`,
    studio.city_name,
    formatOwnerLabel(studio),
    studio.address,
  ].filter(Boolean).join(' · ')
}

function formatStudioMeta(studio: Pick<StudioOption, 'city_name' | 'address' | 'partner_number' | 'persons_fio'>) {
  return [
    studio.city_name,
    formatOwnerLabel(studio),
    studio.address,
  ].filter(Boolean).join(' · ')
}

function formatRub(value: number | null | undefined) {
  if (value == null) return '—'
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value) + ' ₽'
}

function regulationDate(details: RegulationDetails, key: string) {
  const value = details[key]
  return typeof value === 'string' && value ? value : null
}

function formatStudioOptionDetails(studio: Pick<StudioOption, 'partner_number' | 'persons_fio'>) {
  return formatOwnerLabel(studio)
}

function matchesSearch(row: ClosurePreparationRow, query: string) {
  return row.studio_number.includes(query)
    || (row.city_name ?? '').toLowerCase().includes(query)
    || (row.address ?? '').toLowerCase().includes(query)
    || (row.persons_fio ?? '').toLowerCase().includes(query)
    || (row.partner_number == null ? '' : String(row.partner_number)).includes(query)
    || (row.created_by_name ?? '').toLowerCase().includes(query)
    || (row.case_manager_name ?? '').toLowerCase().includes(query)
    || (row.signal_source ?? '').toLowerCase().includes(query)
    || (row.closure_reason ?? '').toLowerCase().includes(query)
    || (row.negotiation_summary ?? '').toLowerCase().includes(query)
    || (row.note ?? '').toLowerCase().includes(query)
}

function warningLabelsForRow(row: ClosurePreparationRow) {
  return [
    row.warning_access_lost || row.warning_socials_removed ? 'У УК потерян доступ к соцсетям/системам' : null,
    row.warning_partner_unreachable ? 'Франчайзи не выходит на связь' : null,
  ].filter((label): label is string => Boolean(label))
}

const SELECTABLE_STUDIO_STATUSES = new Set(['OPEN', 'SUSPENDED', 'SERVICE', 'SWITCH_TO_PURCHASE', 'CLOSED'])
const FACTUALLY_CLOSED_STUDIO_STATUSES = new Set(['CLOSED', 'CANCELLED'])

// Зеркало серверного гарда complete_studio_closure_preparation: показываем, чего
// не хватает, до нажатия. Источник правды остаётся на сервере — кнопка лишь
// не даёт кликнуть заведомо в ошибку.
function missingForCompletion(row: {
  status_code: string | null
  owner_official_notice_received: boolean
  owner_official_notice_received_on: string | null
  closure_confirmation_received: boolean
  closure_confirmation_received_on: string | null
  termination_signed: boolean
  termination_signed_on: string | null
  legal_unilateral_sent_on: string | null
  contract_expiry_on: string | null
}) {
  const status = (row.status_code ?? '').toUpperCase()
  const missing: string[] = []
  const unilateralTerminationSent = Boolean(row.legal_unilateral_sent_on)
  const unilateralTerminationInFuture = Boolean(row.legal_unilateral_sent_on && row.legal_unilateral_sent_on > todayIso())
  const contractExpired = Boolean(row.contract_expiry_on && row.contract_expiry_on <= todayIso())
  if (unilateralTerminationInFuture) missing.push('дата отправки одностороннего расторжения не может быть в будущем')
  if (!unilateralTerminationSent && !contractExpired && !row.owner_official_notice_received) missing.push('официальное уведомление владельца')
  else if (!unilateralTerminationSent && !contractExpired && !row.owner_official_notice_received_on) missing.push('дата уведомления владельца')
  if (!unilateralTerminationSent && !row.closure_confirmation_received) missing.push('подтверждение закрытия')
  else if (!unilateralTerminationSent && !row.closure_confirmation_received_on) missing.push('дата подтверждения закрытия')
  if (!unilateralTerminationSent && !contractExpired && !row.termination_signed) missing.push('подписанное расторжение, дата одностороннего расторжения или завершившийся ДКК')
  else if (!unilateralTerminationSent && !contractExpired && !row.termination_signed_on) missing.push('дата подписания расторжения')
  if (!FACTUALLY_CLOSED_STUDIO_STATUSES.has(status)) {
    missing.push(`фактический статус студии (сейчас ${status || 'не указан'}, нужен CLOSED или CANCELLED)`)
  }
  return missing
}

export function StudioClosurePreparationsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const selectorRef = useRef<HTMLDivElement>(null)

  const [studios, setStudios] = useState<StudioOption[]>([])
  const [rows, setRows] = useState<ClosurePreparationRow[]>([])
  const [caseManagers, setCaseManagers] = useState<CaseManagerOption[]>([])
  const [caseManagerSuggestions, setCaseManagerSuggestions] = useState<Map<string, CaseManagerSuggestion>>(new Map())
  const [regulationDetailsByStudio, setRegulationDetailsByStudio] = useState<Record<string, RegulationDetails>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [endingId, setEndingId] = useState<number | null>(null)
  const [cancelTarget, setCancelTarget] = useState<ClosurePreparationRow | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [quickFilter, setQuickFilter] = useState<'all' | 'overdue' | 'waiting_partner' | 'sale' | 'warnings'>('all')
  const [caseMode, setCaseMode] = useState<ClosureCaseMode>('manage')
  const [activeFormStep, setActiveFormStep] = useState<ClosureFormStep>('signal')
  const [caseEditorOpen, setCaseEditorOpen] = useState(false)
  const [bilateralTerminationOpen, setBilateralTerminationOpen] = useState(true)
  const [unilateralTerminationOpen, setUnilateralTerminationOpen] = useState(false)
  const [contractExpiryTerminationOpen, setContractExpiryTerminationOpen] = useState(false)
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false)
  const [emergencyIntroVisible, setEmergencyIntroVisible] = useState(false)
  const [unsavedCloseTarget, setUnsavedCloseTarget] = useState<'case' | 'department' | null>(null)
  const [statusHandoffTarget, setStatusHandoffTarget] = useState<StatusHandoffTarget | null>(null)
  const [statusHandoffComment, setStatusHandoffComment] = useState('')
  const [statusHandoffClosureOn, setStatusHandoffClosureOn] = useState('')
  const [statusHandoffClosureType, setStatusHandoffClosureType] = useState<StudioClosureType>('STANDARD')
  const [statusHandoffSubmitting, setStatusHandoffSubmitting] = useState(false)
  const [departmentModalRow, setDepartmentModalRow] = useState<ClosurePreparationRow | null>(null)
  const closeAfterSaveRef = useRef<'case' | 'department' | null>(null)
  const emergencySaveRef = useRef(false)
  const caseEditorContentRef = useRef<HTMLDivElement>(null)
  const departmentModalContentRef = useRef<HTMLDivElement>(null)
  const initialFormSnapshotRef = useRef<string | null>(null)
  const [activeDepartmentBlock, setActiveDepartmentBlock] = useState<DepartmentBlockId>('legal')
  const [studioQuery, setStudioQuery] = useState('')
  const [studioSelectorOpen, setStudioSelectorOpen] = useState(false)
  const [relatedStudiosOpen, setRelatedStudiosOpen] = useState(false)
  const [additionalStudioNumbers, setAdditionalStudioNumbers] = useState<string[]>([])
  const [inactiveOpen, setInactiveOpen] = useState(false)

  const [studioNumber, setStudioNumber] = useState('')
  const [preparationStartedOn, setPreparationStartedOn] = useState(todayIso())
  const [expectedClosureOn, setExpectedClosureOn] = useState('')
  const [noticeReceived, setNoticeReceived] = useState(false)
  const [noticeReceivedOn, setNoticeReceivedOn] = useState('')
  const [closureConfirmationReceived, setClosureConfirmationReceived] = useState(false)
  const [closureConfirmationReceivedOn, setClosureConfirmationReceivedOn] = useState('')
  const [terminationSigned, setTerminationSigned] = useState(false)
  const [terminationSignedOn, setTerminationSignedOn] = useState('')
  const [note, setNote] = useState('')
  const [caseKind, setCaseKind] = useState<ClosureCaseKind>('SINGLE_STUDIO_CLOSURE')
  const [signalSource, setSignalSource] = useState('')
  const [caseManagerName, setCaseManagerName] = useState('')
  const [caseManagerUserId, setCaseManagerUserId] = useState('')
  const [partnerContactStatus, setPartnerContactStatus] = useState<PartnerContactStatus | ''>('')
  const [closureReason, setClosureReason] = useState('')
  const [negotiationSummary, setNegotiationSummary] = useState('')
  const [managementDecision, setManagementDecision] = useState<ManagementDecision | ''>('')
  const [managementDecisionOn, setManagementDecisionOn] = useState('')
  const [managementDecisionNote, setManagementDecisionNote] = useState('')
  const [managementOnlineHelpDecision, setManagementOnlineHelpDecision] = useState<OnlineHelpDecision | ''>('')
  const [managementOnlineHelpUntil, setManagementOnlineHelpUntil] = useState('')
  const [managementSeveranceTerms, setManagementSeveranceTerms] = useState('')
  const [managementSaveAttemptRequired, setManagementSaveAttemptRequired] = useState(false)
  const [hallsCount, setHallsCount] = useState('')
  const [otherDebtAmount, setOtherDebtAmount] = useState('')
  const [otherDebtNote, setOtherDebtNote] = useState('')
  const [partnerAgreementsSummary, setPartnerAgreementsSummary] = useState('')
  const [importantInformation, setImportantInformation] = useState('')
  const [closureDecision, setClosureDecision] = useState('')
  const [partnerHelpDecision, setPartnerHelpDecision] = useState('')
  const [partnerHelpType, setPartnerHelpType] = useState('')
  const [partnerHelpForm, setPartnerHelpForm] = useState('')
  const [partnerHelpTerms, setPartnerHelpTerms] = useState('')
  const [partnerHelpAssignee, setPartnerHelpAssignee] = useState('')
  const [partnerClosureTermsCommunicated, setPartnerClosureTermsCommunicated] = useState(false)
  const [partnerCloseDateCommunicated, setPartnerCloseDateCommunicated] = useState(false)
  const [partnerActionsCommunicated, setPartnerActionsCommunicated] = useState(false)
  const [partnerCompletionTermsCommunicated, setPartnerCompletionTermsCommunicated] = useState(false)
  const [partnerRequiredInfoReceived, setPartnerRequiredInfoReceived] = useState(false)
  const [legalStatusReadyForClosureSet, setLegalStatusReadyForClosureSet] = useState(false)
  const [legalCorporateEmailSentOn, setLegalCorporateEmailSentOn] = useState('')
  const [legalLmChatNotifiedOn, setLegalLmChatNotifiedOn] = useState('')
  const [stakeholdersNotifiedOn, setStakeholdersNotifiedOn] = useState('')
  const [noSalesInformationSentOn, setNoSalesInformationSentOn] = useState('')
  const [closedInformationSentOn, setClosedInformationSentOn] = useState('')
  const [pedagogyTrainingMonitoringContinued, setPedagogyTrainingMonitoringContinued] = useState(false)
  const [pedagogySecretFeedbackSearchStopped, setPedagogySecretFeedbackSearchStopped] = useState(false)
  const [caseScenarioStatus, setCaseScenarioStatus] = useState<CaseScenarioStatus | ''>('')
  const [caseScenarioChangedOn, setCaseScenarioChangedOn] = useState('')
  const [caseScenarioNote, setCaseScenarioNote] = useState('')
  const [financeLmReconciled, setFinanceLmReconciled] = useState(false)
  const [financeChiefAccountantConfirmed, setFinanceChiefAccountantConfirmed] = useState(false)
  const [financeRoyaltyDebtSystem, setFinanceRoyaltyDebtSystem] = useState('')
  const [financeRoyaltyDebtAccounting, setFinanceRoyaltyDebtAccounting] = useState('')
  const [financeLmReconciliationNote, setFinanceLmReconciliationNote] = useState('')
  const [legalTerminationPreparedOn, setLegalTerminationPreparedOn] = useState('')
  const [legalTerminationSentOn, setLegalTerminationSentOn] = useState('')
  const [legalSignDueOn, setLegalSignDueOn] = useState('')
  const [legalUnilateralSentOn, setLegalUnilateralSentOn] = useState('')
  const [contractExpiryOn, setContractExpiryOn] = useState('')
  const [technicalChecklist, setTechnicalChecklist] = useState<Record<string, boolean>>({})
  const initialTechnicalChecklistRef = useRef<Record<string, boolean>>({})
  const [techChecklistOpen, setTechChecklistOpen] = useState(false)
  const [techLegalNotifiedOn, setTechLegalNotifiedOn] = useState('')
  const [partnerMessageSentOn, setPartnerMessageSentOn] = useState('')
  const [partnerMessageChannel, setPartnerMessageChannel] = useState('')
  const [partnerMessageAddress, setPartnerMessageAddress] = useState('')
  const [partnerResponseDueOn, setPartnerResponseDueOn] = useState('')
  const [partnerMessageSentConfirmed, setPartnerMessageSentConfirmed] = useState(false)
  const [partnerMessageReceivedConfirmed, setPartnerMessageReceivedConfirmed] = useState(false)
  const [partnerNoticeMissingReason, setPartnerNoticeMissingReason] = useState('')
  const [clientLiabilityAmount, setClientLiabilityAmount] = useState('')
  const [clientLiabilityCount, setClientLiabilityCount] = useState('')
  const [clientLiabilitySnapshotOn, setClientLiabilitySnapshotOn] = useState('')
  const [clientLiabilityNote, setClientLiabilityNote] = useState('')
  const [caseContextOpen, setCaseContextOpen] = useState(false)
  const [warningPasswordsChanged, setWarningPasswordsChanged] = useState(false)
  const [warningChatsRemoved, setWarningChatsRemoved] = useState(false)
  const [warningAccessLost, setWarningAccessLost] = useState(false)
  const [warningPartnerUnreachable, setWarningPartnerUnreachable] = useState(false)
  const [warningNote, setWarningNote] = useState('')
  const [legalBlockStatus, setLegalBlockStatus] = useState<WorkBlockStatus>('NOT_STARTED')
  const [legalBlockDueOn, setLegalBlockDueOn] = useState('')
  const [legalBlockAssignee, setLegalBlockAssignee] = useState('')
  const [legalBlockNote, setLegalBlockNote] = useState('')
  const [legalRegulationSentOn, setLegalRegulationSentOn] = useState('')
  const [legalPartnerResponseDueOn, setLegalPartnerResponseDueOn] = useState('')
  const [legalDocumentsDueOn, setLegalDocumentsDueOn] = useState('')
  const [legalReminderSentOn, setLegalReminderSentOn] = useState('')
  const [legalTerminationPath, setLegalTerminationPath] = useState<TerminationPath>('WAITING')
  const [financeBlockStatus, setFinanceBlockStatus] = useState<WorkBlockStatus>('NOT_STARTED')
  const [financeBlockDueOn, setFinanceBlockDueOn] = useState('')
  const [financeBlockAssignee, setFinanceBlockAssignee] = useState('')
  const [financeBlockNote, setFinanceBlockNote] = useState('')
  const [financeContractsChecked, setFinanceContractsChecked] = useState(false)
  const [financeRevenueChecked, setFinanceRevenueChecked] = useState(false)
  const [financeRoyaltyAccrued, setFinanceRoyaltyAccrued] = useState(false)
  const [financeDebtFixed, setFinanceDebtFixed] = useState(false)
  const [financeLatePaymentsWaitUntil, setFinanceLatePaymentsWaitUntil] = useState('')
  const [techBlockStatus, setTechBlockStatus] = useState<WorkBlockStatus>('NOT_STARTED')
  const [techBlockDueOn, setTechBlockDueOn] = useState('')
  const [techBlockAssignee, setTechBlockAssignee] = useState('')
  const [techBlockNote, setTechBlockNote] = useState('')
  const [techLiabilityPrecheckDone, setTechLiabilityPrecheckDone] = useState(false)
  const [techSystemsTransferReady, setTechSystemsTransferReady] = useState(false)
  const [techListokHideRequested, setTechListokHideRequested] = useState(false)
  const [techAccessesClosedOn, setTechAccessesClosedOn] = useState('')
  const [pedagogyBlockStatus, setPedagogyBlockStatus] = useState<WorkBlockStatus>('NOT_STARTED')
  const [pedagogyBlockDueOn, setPedagogyBlockDueOn] = useState('')
  const [pedagogyBlockAssignee, setPedagogyBlockAssignee] = useState('')
  const [pedagogyBlockNote, setPedagogyBlockNote] = useState('')
  const [pedagogySecretShoppersStopped, setPedagogySecretShoppersStopped] = useState(false)
  const [pedagogyPlansAdjusted, setPedagogyPlansAdjusted] = useState(false)
  const [pedagogyAccessesClosed, setPedagogyAccessesClosed] = useState(false)
  const [salesBlockStatus, setSalesBlockStatus] = useState<WorkBlockStatus>('NOT_REQUIRED')
  const [salesBlockDueOn, setSalesBlockDueOn] = useState('')
  const [salesBlockAssignee, setSalesBlockAssignee] = useState('')
  const [salesBlockNote, setSalesBlockNote] = useState('')
  const [salesPriceAmount, setSalesPriceAmount] = useState('')
  const [salesHasBuyer, setSalesHasBuyer] = useState(false)
  const [salesBuyerSummary, setSalesBuyerSummary] = useState('')
  const [salesTransferDueOn, setSalesTransferDueOn] = useState('')
  const [salesOpeningsHandoffDone, setSalesOpeningsHandoffDone] = useState(false)
  const [salesBuyerDkkSynced, setSalesBuyerDkkSynced] = useState(false)

  const activeRows = useMemo(
    () => rows
      .filter(row => row.preparation_ended_on === null)
      .sort((a, b) => {
        const aDate = a.expected_closure_on ?? '9999-12-31'
        const bDate = b.expected_closure_on ?? '9999-12-31'
        const byExpectedClosure = aDate.localeCompare(bDate)
        if (byExpectedClosure !== 0) return byExpectedClosure
        return a.preparation_started_on.localeCompare(b.preparation_started_on)
      }),
    [rows]
  )

  const stageCounts = useMemo(
    () => Object.fromEntries(
      CLOSURE_FORM_STEPS.map(step => [step.id, activeRows.filter(row => (
        row.current_stage === step.id
        || ((step.id === 'termination' || step.id === 'technical')
          && regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'CLOSURE_CONFIRMED')
      )).length])
    ) as Record<string, number>,
    [activeRows, regulationDetailsByStudio]
  )

  const emergencyStageCounts = useMemo(
    () => Object.fromEntries(
      ['termination', 'technical'].map(stage => [
        stage,
        activeRows.filter(row => (
          regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'CLOSURE_CONFIRMED'
        )).length,
      ])
    ) as Record<string, number>,
    [activeRows, regulationDetailsByStudio]
  )

  const warningStageCounts = useMemo(
    () => Object.fromEntries(
      CLOSURE_FORM_STEPS.map(step => [
        step.id,
        activeRows.filter(row => (
          row.current_stage === step.id
          && (warningLabelsForRow(row).length > 0
            || regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'REBRANDING')
        )).length,
      ])
    ) as Record<string, number>,
    [activeRows, regulationDetailsByStudio]
  )

  const selectedActiveRow = useMemo(
    () => activeRows.find(row => row.studio_number === studioNumber) ?? null,
    [activeRows, studioNumber]
  )

  const stats = useMemo(() => {
    const overdue = activeRows.filter(row => {
      const days = daysUntil(row.expected_closure_on)
      return days !== null && days < 0
    }).length
    const dueSoon = activeRows.filter(row => {
      const days = daysUntil(row.expected_closure_on)
      return days !== null && days >= 0 && days <= 28
    }).length
    const missingNotice = activeRows.filter(row => {
      const days = daysUntil(row.expected_closure_on)
      return !row.owner_official_notice_received && days !== null && days <= 14
    }).length
    const blockOverdue = activeRows.filter(row =>
      blockDueOverdue(row.legal_block_status, row.legal_block_due_on)
      || blockDueOverdue(row.finance_block_status, row.finance_block_due_on)
      || blockDueOverdue(row.tech_block_status, row.tech_block_due_on)
      || blockDueOverdue(row.pedagogy_block_status, row.pedagogy_block_due_on)
      || blockDueOverdue(row.sales_block_status, row.sales_block_due_on)
      || blockDueOverdue('IN_PROGRESS', row.partner_response_due_on)
    ).length
    return { active: activeRows.length, dueSoon, overdue, missingNotice, blockOverdue }
  }, [activeRows])

  const filteredActiveRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return activeRows.filter(row => {
      const isEmergencyFinalStage = (activeFormStep === 'termination' || activeFormStep === 'technical')
        && regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'CLOSURE_CONFIRMED'
      const matchesStage = row.current_stage === activeFormStep || isEmergencyFinalStage
      const matchesQuery = !q || matchesSearch(row, q)
      const matchesFilter = quickFilter === 'all'
        || (quickFilter === 'sale' && row.case_kind === 'STUDIO_SALE')
        || (quickFilter === 'warnings' && (
          warningLabelsForRow(row).length > 0
          || regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'REBRANDING'
        ))
        || (quickFilter === 'waiting_partner' && [
          row.legal_block_status,
          row.finance_block_status,
          row.tech_block_status,
          row.pedagogy_block_status,
          row.sales_block_status,
        ].includes('WAITING_PARTNER'))
        || (quickFilter === 'overdue' && (
          daysUntil(row.expected_closure_on) !== null && daysUntil(row.expected_closure_on)! < 0
          || blockDueOverdue(row.legal_block_status, row.legal_block_due_on)
          || blockDueOverdue(row.finance_block_status, row.finance_block_due_on)
          || blockDueOverdue(row.tech_block_status, row.tech_block_due_on)
          || blockDueOverdue(row.pedagogy_block_status, row.pedagogy_block_due_on)
          || blockDueOverdue(row.sales_block_status, row.sales_block_due_on)
          || blockDueOverdue('IN_PROGRESS', row.partner_response_due_on)
        ))
      return matchesStage && matchesQuery && matchesFilter
    })
  }, [activeFormStep, activeRows, quickFilter, regulationDetailsByStudio, search])

  const inactiveRows = useMemo(
    () => rows
      .filter(row => row.preparation_ended_on !== null)
      .sort((a, b) => {
        const aEnded = a.preparation_ended_on ?? '0000-00-00'
        const bEnded = b.preparation_ended_on ?? '0000-00-00'
        const byEnded = bEnded.localeCompare(aEnded)
        if (byEnded !== 0) return byEnded
        return b.id - a.id
      }),
    [rows]
  )

  const filteredInactiveRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return inactiveRows
    return inactiveRows.filter(row => matchesSearch(row, q))
  }, [inactiveRows, search])

  const filteredStudioOptions = useMemo(() => {
    const q = studioQuery.trim().toLowerCase()
    const base = q
      ? studios.filter(studio => {
          const values = [
            studio.studio_number,
            studio.city_name,
            studio.address,
            studio.partner_number == null ? null : String(studio.partner_number),
            studio.persons_fio,
          ]
          return values.some(value => (value ?? '').toLowerCase().includes(q))
        })
      : studios

    return base.slice(0, 60)
  }, [studios, studioQuery])

  const groupedStudioOptions = useMemo(() => {
    const map = new Map<string, StudioOption[]>()
    for (const studio of filteredStudioOptions) {
      const city = studio.city_name || 'Город не указан'
      if (!map.has(city)) map.set(city, [])
      map.get(city)!.push(studio)
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, 'ru', { sensitivity: 'base' })
    )
  }, [filteredStudioOptions])

  const selectedStudioOption = useMemo(
    () => studios.find(studio => studio.studio_number === studioNumber) ?? null,
    [studios, studioNumber]
  )

  const relatedStudioOptions = useMemo(() => {
    if (!selectedStudioOption?.partner_number) return []
    return studios.filter(studio => (
      studio.partner_number === selectedStudioOption.partner_number
      && studio.studio_number !== selectedStudioOption.studio_number
      && !activeRows.some(row => row.studio_number === studio.studio_number)
    ))
  }, [activeRows, selectedStudioOption, studios])

  const formWarnings = useMemo(() => {
    const warnings: string[] = []
    const selectedStatus = (selectedActiveRow?.status_code ?? selectedStudioOption?.status_code ?? '').toUpperCase()

    if (noticeReceived && !noticeReceivedOn) {
      warnings.push('Официальное уведомление отмечено, но дата уведомления не указана.')
    }
    if (closureConfirmationReceived && !closureConfirmationReceivedOn) {
      warnings.push('Подтверждение закрытия отмечено, но дата подтверждения не указана.')
    }
    if (
      closureConfirmationReceived
      && selectedStatus
      && !FACTUALLY_CLOSED_STUDIO_STATUSES.has(selectedStatus)
    ) {
      warnings.push('Подтверждение закрытия отмечено, но фактический статус студии ещё не закрытый. Запись останется активной для алерта.')
    }
    if (terminationSigned && !terminationSignedOn) {
      warnings.push('Расторжение отмечено, но дата подписания не указана.')
    }

    if (preparationStartedOn && expectedClosureOn && expectedClosureOn < preparationStartedOn) {
      warnings.push('Ожидаемая дата закрытия раньше даты ввода статуса.')
    }
    if (preparationStartedOn && closureConfirmationReceivedOn && closureConfirmationReceivedOn < preparationStartedOn) {
      warnings.push('Дата подтверждения раньше даты ввода статуса.')
    }
    if (preparationStartedOn && terminationSignedOn && terminationSignedOn < preparationStartedOn) {
      warnings.push('Дата расторжения раньше даты ввода статуса.')
    }
    if (closureConfirmationReceivedOn && terminationSignedOn && terminationSignedOn < closureConfirmationReceivedOn) {
      warnings.push('Дата расторжения раньше даты подтверждения закрытия.')
    }

    return warnings
  }, [
    closureConfirmationReceived,
    closureConfirmationReceivedOn,
    expectedClosureOn,
    noticeReceived,
    noticeReceivedOn,
    preparationStartedOn,
    selectedActiveRow?.status_code,
    selectedStudioOption?.status_code,
    terminationSigned,
    terminationSignedOn,
  ])

  const loadData = async () => {
    if (!user?.id) return
    setLoading(true)
    setError(null)
    try {
      const [studiosResult, preparationsResult, stagesResult, managersResult, assignmentsResult, suggestionsResult] = await Promise.all([
        (supabase as any).rpc('list_studio_closure_studio_options'),
        (supabase as any).rpc('list_studio_closure_preparations'),
        (supabase as any).rpc('list_studio_closure_preparation_stages'),
        listMcUsersForTuAdmin(),
        (supabase as any).rpc('list_studio_closure_case_manager_assignments'),
        (supabase as any).rpc('list_studio_closure_case_manager_suggestions'),
      ])

      if (studiosResult.error) throw studiosResult.error
      if (preparationsResult.error) throw preparationsResult.error
      if (stagesResult.error) throw stagesResult.error
      if (assignmentsResult.error) throw assignmentsResult.error
      if (suggestionsResult.error) throw suggestionsResult.error

      const studioOptions = ((studiosResult.data ?? []) as StudioOption[])
        .map(studio => ({
          ...studio,
          studio_number: String(studio.studio_number),
        }))
        .filter(studio => SELECTABLE_STUDIO_STATUSES.has(studio.status_code || 'OPEN'))
        .sort((a, b) => Number(a.studio_number) - Number(b.studio_number))

      const assignmentsByStudio = new Map(
        ((assignmentsResult.data ?? []) as Array<{
          studio_number: string
          case_manager_user_id: string | null
          case_manager_name: string | null
        }>).map(assignment => [String(assignment.studio_number), assignment])
      )
      const preparationRows = ((preparationsResult.data ?? []) as ClosurePreparationRow[]).map(row => ({
        ...row,
        current_stage: ((stagesResult.data ?? []) as Array<{ studio_number: string; current_stage: ClosureFormStep }>)
          .find(stage => String(stage.studio_number) === String(row.studio_number))?.current_stage ?? 'decision',
        ...(assignmentsByStudio.get(String(row.studio_number)) ?? {}),
      }))
      const [regulationResult, handoffResult, checklistResult] = await Promise.all([
        (supabase as any).rpc('list_studio_closure_preparation_regulations', {
          p_studio_numbers: preparationRows.map(row => Number(row.studio_number)),
        }),
        (supabase as any).rpc('list_studio_closure_status_handoffs', {
          p_studio_numbers: preparationRows.map(row => Number(row.studio_number)),
        }),
        (supabase as any).rpc('list_studio_closure_technical_checklists', {
          p_studio_numbers: preparationRows.map(row => Number(row.studio_number)),
        }),
      ])
      const { data: regulationRows, error: regulationError } = regulationResult
      const { data: handoffRows, error: handoffError } = handoffResult
      const { data: checklistRows, error: checklistError } = checklistResult
      if (regulationError) throw regulationError
      if (handoffError) throw handoffError
      if (checklistError) throw checklistError

      const handoffDetailsByStudio = Object.fromEntries(
        ((handoffRows ?? []) as Array<{ studio_number: string; details: RegulationDetails }>).map(row => [row.studio_number, row.details ?? {}])
      ) as Record<string, RegulationDetails>
      const checklistByStudio = Object.fromEntries(
        ((checklistRows ?? []) as Array<{ studio_number: string; checklist: Record<string, boolean> }>).map(row => [row.studio_number, row.checklist ?? {}])
      ) as Record<string, Record<string, boolean>>

      setStudios(studioOptions)
      setCaseManagers(managersResult.filter(isCaseManagerCandidate))
      setCaseManagerSuggestions(new Map(
        ((suggestionsResult.data ?? []) as CaseManagerSuggestion[]).map(suggestion => [String(suggestion.studio_number), suggestion])
      ))
      setRows(preparationRows)
      setRegulationDetailsByStudio(Object.fromEntries(
        ((regulationRows ?? []) as Array<{ studio_number: string; details: RegulationDetails }>).map(row => [
          row.studio_number,
          { ...(row.details ?? {}), ...(handoffDetailsByStudio[row.studio_number] ?? {}), technical_checklist: checklistByStudio[row.studio_number] ?? {} },
        ])
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [user?.id])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setStudioSelectorOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!caseEditorOpen && !departmentModalRow) {
      initialFormSnapshotRef.current = null
      return
    }

    const frame = window.requestAnimationFrame(() => {
      const container = departmentModalRow ? departmentModalContentRef.current : caseEditorContentRef.current
      if (!container) return
      initialFormSnapshotRef.current = JSON.stringify(
        Array.from(container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea'))
          .map(control => ({
            name: control.name,
            type: control instanceof HTMLInputElement ? control.type : '',
            value: control instanceof HTMLInputElement && control.type === 'checkbox' ? control.checked : control.value,
          }))
      )
    })

    return () => window.cancelAnimationFrame(frame)
  }, [caseEditorOpen, departmentModalRow])

  const selectStudio = (studio: StudioOption) => {
    setFormDirty(true)
    setStudioNumber(studio.studio_number)
    setStudioQuery(formatStudioLabel(studio))
    setAdditionalStudioNumbers([])
    if (caseMode === 'create' || !caseManagerName.trim()) {
      const suggestion = caseManagerSuggestions.get(String(studio.studio_number))
      setCaseManagerName(suggestion?.case_manager_name || studio.suggested_case_manager_name || 'Татьяна Копотун')
      setCaseManagerUserId(suggestion?.case_manager_user_id ?? '')
    }
    setStudioSelectorOpen(false)
  }

  const loadRegulationDetails = async (number: string) => {
    const [regulationResult, handoffResult, technicalChecklistResult] = await Promise.all([
      (supabase as any).rpc('get_studio_closure_preparation_regulation', {
        p_studio_number: Number(number),
      }),
      (supabase as any).rpc('get_studio_closure_status_handoffs', {
        p_studio_number: Number(number),
      }),
      (supabase as any).rpc('get_studio_closure_technical_checklist', {
        p_studio_number: Number(number),
      }),
    ])
    const { data, error: detailsError } = regulationResult
    const { data: handoffData, error: handoffError } = handoffResult
    const { data: checklistData, error: checklistError } = technicalChecklistResult
    if (detailsError) throw detailsError
    if (handoffError) throw handoffError
    if (checklistError) throw checklistError
    const details: Record<string, unknown> = { ...((data ?? {}) as Record<string, unknown>), ...((handoffData ?? {}) as Record<string, unknown>), technical_checklist: checklistData ?? {} }
    setHallsCount(details.halls_count == null ? '' : String(details.halls_count))
    setOtherDebtAmount(details.other_debt_amount == null ? '' : String(details.other_debt_amount))
    setOtherDebtNote(String(details.other_debt_note ?? ''))
    setPartnerAgreementsSummary(String(details.partner_agreements_summary ?? ''))
    setImportantInformation(String(details.important_information ?? ''))
    setClosureDecision(String(details.closure_decision ?? ''))
    setPartnerHelpDecision(String(details.partner_help_decision ?? ''))
    setPartnerHelpType(String(details.partner_help_type ?? ''))
    setPartnerHelpForm(String(details.partner_help_form ?? ''))
    setPartnerHelpTerms(String(details.partner_help_terms ?? ''))
    setPartnerHelpAssignee(String(details.partner_help_assignee ?? ''))
    setPartnerClosureTermsCommunicated(Boolean(details.partner_closure_terms_communicated))
    setPartnerCloseDateCommunicated(Boolean(details.partner_close_date_communicated))
    setPartnerActionsCommunicated(Boolean(details.partner_actions_communicated))
    setPartnerCompletionTermsCommunicated(Boolean(details.partner_completion_terms_communicated))
    setPartnerRequiredInfoReceived(Boolean(details.partner_required_info_received))
    setLegalStatusReadyForClosureSet(Boolean(details.legal_status_ready_for_closure_set))
    setLegalCorporateEmailSentOn(String(details.legal_corporate_email_sent_on ?? ''))
    setLegalLmChatNotifiedOn(String(details.legal_lm_chat_notified_on ?? ''))
    setStakeholdersNotifiedOn(String(details.stakeholders_notified_on ?? ''))
    setNoSalesInformationSentOn(String(details.status_no_sales_information_sent_on ?? ''))
    setClosedInformationSentOn(String(details.status_closed_information_sent_on ?? ''))
    setPedagogyTrainingMonitoringContinued(Boolean(details.pedagogy_training_monitoring_continued))
    setPedagogySecretFeedbackSearchStopped(Boolean(details.pedagogy_secret_feedback_search_stopped))
    setCaseScenarioStatus(String(details.case_scenario_status ?? '') as CaseScenarioStatus | '')
    setCaseScenarioChangedOn(String(details.case_scenario_changed_on ?? ''))
    setCaseScenarioNote(String(details.case_scenario_note ?? ''))
    setFinanceLmReconciled(Boolean(details.finance_lm_reconciled))
    setFinanceChiefAccountantConfirmed(Boolean(details.finance_chief_accountant_confirmed))
    setFinanceRoyaltyDebtSystem(details.finance_royalty_debt_system == null ? '' : String(details.finance_royalty_debt_system))
    setFinanceRoyaltyDebtAccounting(details.finance_royalty_debt_accounting == null ? '' : String(details.finance_royalty_debt_accounting))
    setFinanceLmReconciliationNote(String(details.finance_lm_reconciliation_note ?? ''))
    setLegalTerminationPreparedOn(String(details.legal_termination_prepared_on ?? ''))
    setLegalTerminationSentOn(String(details.legal_termination_sent_on ?? ''))
    setLegalSignDueOn(String(details.legal_sign_due_on ?? ''))
    setLegalUnilateralSentOn(String(details.legal_unilateral_sent_on ?? ''))
    setContractExpiryOn(String(details.contract_expiry_on ?? ''))
    const loadedChecklist = (details.technical_checklist ?? {}) as Record<string, boolean>
    initialTechnicalChecklistRef.current = loadedChecklist
    setTechnicalChecklist(loadedChecklist)
    setTechLegalNotifiedOn(String(details.tech_legal_notified_on ?? ''))
  }

  const fillFromRow = (row: ClosurePreparationRow, openEditor = true) => {
    setFormDirty(false)
    setCaseMode('manage')
    setActiveFormStep(row.current_stage ?? 'decision')
    setStudioNumber(row.studio_number)
    setStudioQuery(formatStudioLabel(row))
    setPreparationStartedOn(row.preparation_started_on)
    setExpectedClosureOn(row.expected_closure_on ?? '')
    setNoticeReceived(row.owner_official_notice_received)
    setNoticeReceivedOn(row.owner_official_notice_received_on ?? '')
    setClosureConfirmationReceived(row.closure_confirmation_received)
    setClosureConfirmationReceivedOn(row.closure_confirmation_received_on ?? '')
    setTerminationSigned(row.termination_signed)
    setTerminationSignedOn(row.termination_signed_on ?? '')
    setNote(row.note ?? '')
    setCaseKind(row.case_kind ?? 'SINGLE_STUDIO_CLOSURE')
    setSignalSource(row.signal_source ?? '')
    const suggestedManager = caseManagerSuggestions.get(String(row.studio_number))
    setCaseManagerName(row.case_manager_name ?? suggestedManager?.case_manager_name ?? '')
    setCaseManagerUserId(row.case_manager_user_id ?? suggestedManager?.case_manager_user_id ?? '')
    setPartnerContactStatus(row.partner_contact_status ?? '')
    setClosureReason(row.closure_reason ?? '')
    setNegotiationSummary(row.negotiation_summary ?? '')
    setManagementDecision(row.management_decision ?? '')
    setManagementDecisionOn(row.management_decision_on ?? '')
    setManagementDecisionNote(row.management_decision_note ?? '')
    setManagementOnlineHelpDecision(row.management_online_help_decision ?? '')
    setManagementOnlineHelpUntil(row.management_online_help_until ?? '')
    setManagementSeveranceTerms(row.management_severance_terms ?? '')
    setManagementSaveAttemptRequired(row.management_save_attempt_required)
    setPartnerMessageSentOn(row.partner_message_sent_on ?? '')
    setPartnerMessageChannel(row.partner_message_channel ?? '')
    setPartnerMessageAddress(row.partner_message_address ?? '')
    setPartnerResponseDueOn(row.partner_response_due_on ?? '')
    setPartnerMessageSentConfirmed(row.partner_message_sent_confirmed)
    setPartnerMessageReceivedConfirmed(row.partner_message_received_confirmed)
    setPartnerNoticeMissingReason(row.partner_notice_missing_reason ?? '')
    setClientLiabilityAmount(row.client_liability_amount == null ? '' : String(row.client_liability_amount))
    setClientLiabilityCount(row.client_liability_count == null ? '' : String(row.client_liability_count))
    setClientLiabilitySnapshotOn(row.client_liability_snapshot_on ?? '')
    setClientLiabilityNote(row.client_liability_note ?? '')
    setCaseContextOpen(false)
    setWarningPasswordsChanged(row.warning_passwords_changed)
    setWarningChatsRemoved(row.warning_chats_removed)
    setWarningAccessLost(row.warning_access_lost || row.warning_socials_removed)
    setWarningPartnerUnreachable(row.warning_partner_unreachable)
    setWarningNote(row.warning_note ?? '')
    setLegalBlockStatus(row.legal_block_status ?? 'NOT_STARTED')
    setLegalBlockDueOn(row.legal_block_due_on ?? '')
    setLegalBlockAssignee(row.legal_block_assignee ?? '')
    setLegalBlockNote(row.legal_block_note ?? '')
    setLegalRegulationSentOn(row.legal_regulation_sent_on ?? '')
    setLegalPartnerResponseDueOn(row.legal_partner_response_due_on ?? '')
    setLegalDocumentsDueOn(row.legal_documents_due_on ?? '')
    setLegalReminderSentOn(row.legal_reminder_sent_on ?? '')
    setLegalTerminationPath(row.legal_termination_path ?? 'WAITING')
    setFinanceBlockStatus(row.finance_block_status ?? 'NOT_STARTED')
    setFinanceBlockDueOn(row.finance_block_due_on ?? '')
    setFinanceBlockAssignee(row.finance_block_assignee ?? '')
    setFinanceBlockNote(row.finance_block_note ?? '')
    setFinanceContractsChecked(row.finance_contracts_checked)
    setFinanceRevenueChecked(row.finance_revenue_checked)
    setFinanceRoyaltyAccrued(row.finance_royalty_accrued)
    setFinanceDebtFixed(row.finance_debt_fixed)
    setFinanceLatePaymentsWaitUntil(row.finance_late_payments_wait_until ?? '')
    setTechBlockStatus(row.tech_block_status ?? 'NOT_STARTED')
    setTechBlockDueOn(row.tech_block_due_on ?? '')
    setTechBlockAssignee(row.tech_block_assignee ?? '')
    setTechBlockNote(row.tech_block_note ?? '')
    setTechLiabilityPrecheckDone(row.tech_liability_precheck_done)
    setTechSystemsTransferReady(row.tech_systems_transfer_ready)
    setTechListokHideRequested(row.tech_listok_hide_requested)
    setTechAccessesClosedOn(row.tech_accesses_closed_on ?? '')
    setPedagogyBlockStatus(row.pedagogy_block_status ?? 'NOT_STARTED')
    setPedagogyBlockDueOn(row.pedagogy_block_due_on ?? '')
    setPedagogyBlockAssignee(row.pedagogy_block_assignee ?? '')
    setPedagogyBlockNote(row.pedagogy_block_note ?? '')
    setPedagogySecretShoppersStopped(row.pedagogy_secret_shoppers_stopped)
    setPedagogyPlansAdjusted(row.pedagogy_plans_adjusted)
    setPedagogyAccessesClosed(row.pedagogy_accesses_closed)
    setSalesBlockStatus(row.sales_block_status ?? 'NOT_REQUIRED')
    setSalesBlockDueOn(row.sales_block_due_on ?? '')
    setSalesBlockAssignee(row.sales_block_assignee ?? '')
    setSalesBlockNote(row.sales_block_note ?? '')
    setSalesPriceAmount(row.sales_price_amount == null ? '' : String(row.sales_price_amount))
    setSalesHasBuyer(row.sales_has_buyer)
    setSalesBuyerSummary(row.sales_buyer_summary ?? '')
    setSalesTransferDueOn(row.sales_transfer_due_on ?? '')
    setSalesOpeningsHandoffDone(row.sales_openings_handoff_done)
    setSalesBuyerDkkSynced(row.sales_buyer_dkk_synced)
    setSuccess(null)
    setError(null)
    void loadRegulationDetails(row.studio_number).catch(err => setError(err instanceof Error ? err.message : String(err)))
    if (openEditor) setCaseEditorOpen(true)
  }

  const openDepartmentsModal = (row: ClosurePreparationRow) => {
    fillFromRow(row, false)
    setActiveFormStep('departments')
    setActiveDepartmentBlock('legal')
    setDepartmentModalRow(row)
  }

  const startNewCase = () => {
    resetForm()
    setCaseEditorOpen(true)
  }

  const resetForm = () => {
    setFormDirty(false)
    setCaseMode('create')
    setActiveFormStep('signal')
    setStudioNumber('')
    setStudioQuery('')
    setRelatedStudiosOpen(false)
    setAdditionalStudioNumbers([])
    setPreparationStartedOn(todayIso())
    setExpectedClosureOn('')
    setNoticeReceived(false)
    setNoticeReceivedOn('')
    setClosureConfirmationReceived(false)
    setClosureConfirmationReceivedOn('')
    setTerminationSigned(false)
    setTerminationSignedOn('')
    setNote('')
    setCaseKind('SINGLE_STUDIO_CLOSURE')
    setSignalSource('')
    setCaseManagerName('')
    setCaseManagerUserId('')
    setPartnerContactStatus('')
    setClosureReason('')
    setNegotiationSummary('')
    setManagementDecision('')
    setManagementDecisionOn('')
    setManagementDecisionNote('')
    setManagementOnlineHelpDecision('')
    setManagementOnlineHelpUntil('')
    setManagementSeveranceTerms('')
    setManagementSaveAttemptRequired(false)
    setHallsCount('')
    setOtherDebtAmount('')
    setOtherDebtNote('')
    setPartnerAgreementsSummary('')
    setImportantInformation('')
    setClosureDecision('')
    setPartnerHelpDecision('')
    setPartnerHelpType('')
    setPartnerHelpForm('')
    setPartnerHelpTerms('')
    setPartnerHelpAssignee('')
    setPartnerClosureTermsCommunicated(false)
    setPartnerCloseDateCommunicated(false)
    setPartnerActionsCommunicated(false)
    setPartnerCompletionTermsCommunicated(false)
    setPartnerRequiredInfoReceived(false)
    setLegalStatusReadyForClosureSet(false)
    setLegalCorporateEmailSentOn('')
    setLegalLmChatNotifiedOn('')
    setStakeholdersNotifiedOn('')
    setNoSalesInformationSentOn('')
    setClosedInformationSentOn('')
    setPedagogyTrainingMonitoringContinued(false)
    setPedagogySecretFeedbackSearchStopped(false)
    setCaseScenarioStatus('')
    setCaseScenarioChangedOn('')
    setCaseScenarioNote('')
    setFinanceLmReconciled(false)
    setFinanceChiefAccountantConfirmed(false)
    setFinanceRoyaltyDebtSystem('')
    setFinanceRoyaltyDebtAccounting('')
    setFinanceLmReconciliationNote('')
    setLegalTerminationPreparedOn('')
    setLegalTerminationSentOn('')
    setLegalSignDueOn('')
    setLegalUnilateralSentOn('')
    setContractExpiryOn('')
    setTechnicalChecklist({})
    initialTechnicalChecklistRef.current = {}
    setTechChecklistOpen(false)
    setTechLegalNotifiedOn('')
    setPartnerMessageSentOn('')
    setPartnerMessageChannel('')
    setPartnerMessageAddress('')
    setPartnerResponseDueOn('')
    setPartnerMessageSentConfirmed(false)
    setPartnerMessageReceivedConfirmed(false)
    setPartnerNoticeMissingReason('')
    setClientLiabilityAmount('')
    setClientLiabilityCount('')
    setClientLiabilitySnapshotOn('')
    setClientLiabilityNote('')
    setCaseContextOpen(false)
    setWarningPasswordsChanged(false)
    setWarningChatsRemoved(false)
    setWarningAccessLost(false)
    setWarningPartnerUnreachable(false)
    setWarningNote('')
    setLegalBlockStatus('NOT_STARTED')
    setLegalBlockDueOn('')
    setLegalBlockAssignee('')
    setLegalBlockNote('')
    setLegalRegulationSentOn('')
    setLegalPartnerResponseDueOn('')
    setLegalDocumentsDueOn('')
    setLegalReminderSentOn('')
    setLegalTerminationPath('WAITING')
    setFinanceBlockStatus('NOT_STARTED')
    setFinanceBlockDueOn('')
    setFinanceBlockAssignee('')
    setFinanceBlockNote('')
    setFinanceContractsChecked(false)
    setFinanceRevenueChecked(false)
    setFinanceRoyaltyAccrued(false)
    setFinanceDebtFixed(false)
    setFinanceLatePaymentsWaitUntil('')
    setTechBlockStatus('NOT_STARTED')
    setTechBlockDueOn('')
    setTechBlockAssignee('')
    setTechBlockNote('')
    setTechLiabilityPrecheckDone(false)
    setTechSystemsTransferReady(false)
    setTechListokHideRequested(false)
    setTechAccessesClosedOn('')
    setPedagogyBlockStatus('NOT_STARTED')
    setPedagogyBlockDueOn('')
    setPedagogyBlockAssignee('')
    setPedagogyBlockNote('')
    setPedagogySecretShoppersStopped(false)
    setPedagogyPlansAdjusted(false)
    setPedagogyAccessesClosed(false)
    setSalesBlockStatus('NOT_REQUIRED')
    setSalesBlockDueOn('')
    setSalesBlockAssignee('')
    setSalesBlockNote('')
    setSalesPriceAmount('')
    setSalesHasBuyer(false)
    setSalesBuyerSummary('')
    setSalesTransferDueOn('')
    setSalesOpeningsHandoffDone(false)
    setSalesBuyerDkkSynced(false)
    setSuccess(null)
    setError(null)
  }

  const save = async (
    targetStep?: ClosureFormStep,
    scenarioOverride?: { status: CaseScenarioStatus | null; changedOn: string; note: string }
  ) => {
    const fallbackStudioNumber = studioQuery.trim().match(/^\#?(\d+)$/)?.[1] ?? ''
    const parsedStudioNumber = Number(studioNumber || fallbackStudioNumber)
    const parsedClientLiabilityAmount = optionalNumber(clientLiabilityAmount)
    const parsedClientLiabilityCount = optionalNumber(clientLiabilityCount)
    const parsedSalesPriceAmount = optionalNumber(salesPriceAmount)
    const parsedHallsCount = optionalNumber(hallsCount)
    const parsedOtherDebtAmount = optionalNumber(otherDebtAmount)
    const scenarioClearFields = scenarioOverride?.status === null
      ? ['case_scenario_status', 'case_scenario_changed_on', 'case_scenario_note']
      : scenarioOverride ? [] : [
      !caseScenarioStatus ? 'case_scenario_status' : null,
      !caseScenarioChangedOn ? 'case_scenario_changed_on' : null,
      !caseScenarioNote.trim() ? 'case_scenario_note' : null,
    ].filter((field): field is string => Boolean(field))
    const secondPassClearFields = [
      !managementOnlineHelpDecision ? 'management_online_help_decision' : null,
      !managementOnlineHelpUntil ? 'management_online_help_until' : null,
      !managementSeveranceTerms.trim() ? 'management_severance_terms' : null,
      !legalRegulationSentOn ? 'legal_regulation_sent_on' : null,
      !legalPartnerResponseDueOn ? 'legal_partner_response_due_on' : null,
      !legalDocumentsDueOn ? 'legal_documents_due_on' : null,
      !legalReminderSentOn ? 'legal_reminder_sent_on' : null,
      legalTerminationPath === 'WAITING' ? 'legal_termination_path' : null,
      !financeLatePaymentsWaitUntil ? 'finance_late_payments_wait_until' : null,
      !techAccessesClosedOn ? 'tech_accesses_closed_on' : null,
      parsedSalesPriceAmount === null ? 'sales_price_amount' : null,
      !salesBuyerSummary.trim() ? 'sales_buyer_summary' : null,
      !salesTransferDueOn ? 'sales_transfer_due_on' : null,
    ].filter((field): field is string => Boolean(field))
    if ((!studioNumber && !fallbackStudioNumber) || Number.isNaN(parsedStudioNumber)) {
      setError('Укажите номер студии.')
      return
    }
    if (!preparationStartedOn) {
      setError('Укажите дату ввода статуса.')
      return
    }
    if (!noticeReceived && noticeReceivedOn) {
      setError('Дата уведомления заполняется только если уведомление получено.')
      return
    }
    if (!closureConfirmationReceived && closureConfirmationReceivedOn) {
      setError('Дата подтверждения заполняется только если подтверждение получено.')
      return
    }
    if (!terminationSigned && terminationSignedOn) {
      setError('Дата расторжения заполняется только если расторжение подписано.')
      return
    }
    if (Number.isNaN(parsedClientLiabilityAmount)) {
      setError('Сумма кредиторки должна быть числом.')
      return
    }
    if (Number.isNaN(parsedClientLiabilityCount) || (parsedClientLiabilityCount !== null && !Number.isInteger(parsedClientLiabilityCount))) {
      setError('Количество клиентов должно быть целым числом.')
      return
    }
    if (Number.isNaN(parsedSalesPriceAmount)) {
      setError('Цена продажи должна быть числом.')
      return
    }

    if (Number.isNaN(parsedHallsCount) || (parsedHallsCount !== null && !Number.isInteger(parsedHallsCount))) {
      setError('Количество залов должно быть целым числом.')
      return
    }
    if (Number.isNaN(parsedOtherDebtAmount) || Number.isNaN(optionalNumber(financeRoyaltyDebtAccounting))) {
      setError('Финансовые суммы должны быть числами.')
      return
    }
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const targetStudioNumbers = caseMode === 'create'
        ? [...new Set([parsedStudioNumber, ...additionalStudioNumbers.map(Number)])]
        : [parsedStudioNumber]
      const automaticManagers = new Map(
        targetStudioNumbers.map(studio => [studio, caseManagerSuggestions.get(String(studio))])
      )
      if (caseMode === 'create') {
        const withoutManager = targetStudioNumbers.filter(studio => !automaticManagers.get(studio)?.case_manager_user_id)
        if (withoutManager.length > 0) {
          throw new Error(`Не удалось автоматически назначить кейс-менеджера для студий: ${withoutManager.join(', ')}.`)
        }
      }
      const closurePayload = {
        p_preparation_started_on: preparationStartedOn,
        p_expected_closure_on: expectedClosureOn || null,
        p_owner_official_notice_received: noticeReceived,
        p_owner_official_notice_received_on: noticeReceived ? (noticeReceivedOn || null) : null,
        p_closure_confirmation_received: closureConfirmationReceived,
        p_closure_confirmation_received_on: closureConfirmationReceived ? (closureConfirmationReceivedOn || null) : null,
        p_termination_signed: terminationSigned,
        p_termination_signed_on: terminationSigned ? (terminationSignedOn || null) : null,
        p_note: note.trim() || null,
        p_case_kind: caseKind,
        p_signal_source: signalSource.trim() || null,
        p_case_manager_name: caseManagerName.trim() || null,
        p_partner_contact_status: partnerContactStatus || null,
        p_closure_reason: closureReason.trim() || null,
        p_negotiation_summary: negotiationSummary.trim() || null,
        p_management_decision: managementDecision || null,
        p_management_decision_on: managementDecisionOn || null,
        p_management_decision_note: managementDecisionNote.trim() || null,
        p_partner_message_sent_on: partnerMessageSentOn || null,
        p_partner_message_channel: partnerMessageChannel.trim() || null,
        p_partner_message_address: partnerMessageAddress.trim() || null,
        p_partner_response_due_on: partnerResponseDueOn || null,
        p_partner_message_sent_confirmed: partnerMessageSentConfirmed,
        p_partner_message_received_confirmed: partnerMessageReceivedConfirmed,
        p_partner_notice_missing_reason: partnerNoticeMissingReason.trim() || null,
        p_client_liability_amount: parsedClientLiabilityAmount,
        p_client_liability_count: parsedClientLiabilityCount,
        p_client_liability_snapshot_on: clientLiabilitySnapshotOn || null,
        p_client_liability_note: clientLiabilityNote.trim() || null,
        p_warning_passwords_changed: warningPasswordsChanged,
        p_warning_chats_removed: warningChatsRemoved,
        p_warning_socials_removed: false,
        p_warning_access_lost: warningAccessLost,
        p_warning_partner_unreachable: warningPartnerUnreachable,
        p_warning_note: warningNote.trim() || null,
        p_legal_block_status: legalBlockStatus,
        p_legal_block_due_on: legalBlockDueOn || null,
        p_legal_block_assignee: legalBlockAssignee.trim() || null,
        p_legal_block_note: legalBlockNote.trim() || null,
        p_finance_block_status: financeBlockStatus,
        p_finance_block_due_on: financeBlockDueOn || null,
        p_finance_block_assignee: financeBlockAssignee.trim() || null,
        p_finance_block_note: financeBlockNote.trim() || null,
        p_tech_block_status: techBlockStatus,
        p_tech_block_due_on: techBlockDueOn || null,
        p_tech_block_assignee: techBlockAssignee.trim() || null,
        p_tech_block_note: techBlockNote.trim() || null,
        p_pedagogy_block_status: pedagogyBlockStatus,
        p_pedagogy_block_due_on: pedagogyBlockDueOn || null,
        p_pedagogy_block_assignee: pedagogyBlockAssignee.trim() || null,
        p_pedagogy_block_note: pedagogyBlockNote.trim() || null,
        p_sales_block_status: salesBlockStatus,
        p_sales_block_due_on: salesBlockDueOn || null,
        p_sales_block_assignee: salesBlockAssignee.trim() || null,
        p_sales_block_note: salesBlockNote.trim() || null,
      }
      for (const targetStudioNumber of targetStudioNumbers) {
        const { error: saveError } = await (supabase as any).rpc('upsert_studio_closure_preparation', {
          ...closurePayload,
          p_studio_number: targetStudioNumber,
        })
        if (saveError) throw saveError
      }
      for (const targetStudioNumber of targetStudioNumbers) {
        const managerUserId = caseMode === 'create'
          ? automaticManagers.get(targetStudioNumber)?.case_manager_user_id
          : caseManagerUserId || null
        const { error: caseManagerError } = await (supabase as any).rpc('set_studio_closure_case_manager', {
          p_studio_number: targetStudioNumber,
          p_case_manager_user_id: managerUserId,
        })
        if (caseManagerError) throw caseManagerError
      }
      const { error: detailsError } = await (supabase as any).rpc('update_studio_closure_preparation_second_pass', {
        p_studio_number: parsedStudioNumber,
        p_management_online_help_decision: managementOnlineHelpDecision || null,
        p_management_online_help_until: managementOnlineHelpUntil || null,
        p_management_severance_terms: managementSeveranceTerms.trim() || null,
        p_management_save_attempt_required: managementSaveAttemptRequired,
        p_legal_regulation_sent_on: legalRegulationSentOn || null,
        p_legal_partner_response_due_on: legalPartnerResponseDueOn || null,
        p_legal_documents_due_on: legalDocumentsDueOn || null,
        p_legal_reminder_sent_on: legalReminderSentOn || null,
        p_legal_termination_path: legalTerminationPath,
        p_finance_contracts_checked: financeContractsChecked,
        p_finance_revenue_checked: financeRevenueChecked,
        p_finance_royalty_accrued: financeRoyaltyAccrued,
        p_finance_debt_fixed: financeDebtFixed,
        p_finance_late_payments_wait_until: financeLatePaymentsWaitUntil || null,
        p_tech_liability_precheck_done: techLiabilityPrecheckDone,
        p_tech_systems_transfer_ready: techSystemsTransferReady,
        p_tech_listok_hide_requested: techListokHideRequested,
        p_tech_accesses_closed_on: techAccessesClosedOn || null,
        p_pedagogy_secret_shoppers_stopped: pedagogySecretShoppersStopped,
        p_pedagogy_plans_adjusted: pedagogyPlansAdjusted,
        p_pedagogy_accesses_closed: pedagogyAccessesClosed,
        p_sales_price_amount: parsedSalesPriceAmount,
        p_sales_has_buyer: salesHasBuyer,
        p_sales_buyer_summary: salesBuyerSummary.trim() || null,
        p_sales_transfer_due_on: salesTransferDueOn || null,
        p_sales_openings_handoff_done: salesOpeningsHandoffDone,
        p_sales_buyer_dkk_synced: salesBuyerDkkSynced,
        p_clear_fields: secondPassClearFields,
      })
      if (detailsError) throw detailsError
      const { error: regulationError } = await (supabase as any).rpc('update_studio_closure_preparation_regulation', {
        p_studio_number: parsedStudioNumber,
        p_halls_count: parsedHallsCount == null ? null : Math.trunc(parsedHallsCount),
        p_other_debt_amount: parsedOtherDebtAmount,
        p_other_debt_note: otherDebtNote.trim() || null,
        p_partner_agreements_summary: partnerAgreementsSummary.trim() || null,
        p_important_information: importantInformation.trim() || null,
        p_closure_decision: closureDecision || null,
        p_partner_help_decision: partnerHelpDecision || null,
        p_partner_help_type: partnerHelpType.trim() || null,
        p_partner_help_form: partnerHelpForm.trim() || null,
        p_partner_help_terms: partnerHelpTerms.trim() || null,
        p_partner_help_assignee: partnerHelpAssignee.trim() || null,
        p_partner_closure_terms_communicated: partnerClosureTermsCommunicated,
        p_partner_close_date_communicated: partnerCloseDateCommunicated,
        p_partner_actions_communicated: partnerActionsCommunicated,
        p_partner_completion_terms_communicated: partnerCompletionTermsCommunicated,
        p_partner_required_info_received: partnerRequiredInfoReceived,
        p_legal_status_ready_for_closure_set: legalStatusReadyForClosureSet,
        p_legal_corporate_email_sent_on: legalCorporateEmailSentOn || null,
        p_legal_lm_chat_notified_on: legalLmChatNotifiedOn || null,
        p_stakeholders_notified_on: stakeholdersNotifiedOn || null,
        p_pedagogy_training_monitoring_continued: pedagogyTrainingMonitoringContinued,
        p_pedagogy_secret_feedback_search_stopped: pedagogySecretFeedbackSearchStopped,
        p_case_scenario_status: scenarioOverride ? scenarioOverride.status : (caseScenarioStatus || null),
        p_case_scenario_changed_on: scenarioOverride ? (scenarioOverride.changedOn || null) : (caseScenarioChangedOn || null),
        p_case_scenario_note: scenarioOverride ? (scenarioOverride.note || null) : (caseScenarioNote.trim() || null),
        p_finance_lm_reconciled: financeLmReconciled,
        p_finance_chief_accountant_confirmed: financeChiefAccountantConfirmed,
        p_finance_royalty_debt_system: financeRoyaltyDebtSystem === '' && currentCaseStudio?.royalty_debt_rub != null
          ? currentCaseStudio.royalty_debt_rub
          : optionalNumber(financeRoyaltyDebtSystem),
        p_finance_royalty_debt_accounting: optionalNumber(financeRoyaltyDebtAccounting),
        p_finance_lm_reconciliation_note: financeLmReconciliationNote.trim() || null,
        p_legal_termination_prepared_on: legalTerminationPreparedOn || null,
        p_legal_termination_sent_on: legalTerminationSentOn || null,
        p_legal_sign_due_on: legalSignDueOn || null,
        p_legal_unilateral_sent_on: legalUnilateralSentOn || null,
        p_tech_legal_notified_on: techLegalNotifiedOn || null,
        p_clear_fields: scenarioClearFields,
      })
      if (regulationError) throw regulationError
      if (activeFormStep === 'technical') {
        const checklistPatch = Object.fromEntries(
          TECH_CLOSURE_ITEMS.filter(item => Boolean(technicalChecklist[item.id]) !== Boolean(initialTechnicalChecklistRef.current[item.id]))
            .map(item => [item.id, Boolean(technicalChecklist[item.id])])
        )
        if (Object.keys(checklistPatch).length > 0) {
          const { error: technicalChecklistError } = await (supabase as any).rpc('update_studio_closure_technical_checklist', {
            p_studio_number: parsedStudioNumber,
            p_checklist: checklistPatch,
          })
          if (technicalChecklistError) throw technicalChecklistError
          initialTechnicalChecklistRef.current = { ...initialTechnicalChecklistRef.current, ...checklistPatch }
        }
      }
      const { error: contractExpiryError } = await (supabase as any).rpc('update_studio_closure_contract_expiry_on', {
        p_studio_number: parsedStudioNumber,
        p_contract_expiry_on: contractExpiryOn || null,
      })
      if (contractExpiryError) throw contractExpiryError
      const handoffClearFields = [
        !noSalesInformationSentOn ? 'status_no_sales_information_sent_on' : null,
        !closedInformationSentOn ? 'status_closed_information_sent_on' : null,
      ].filter((field): field is string => Boolean(field))
      const { error: handoffError } = await (supabase as any).rpc('update_studio_closure_status_handoffs', {
        p_studio_number: parsedStudioNumber,
        p_no_sales_information_sent_on: noSalesInformationSentOn || null,
        p_closed_information_sent_on: closedInformationSentOn || null,
        p_clear_fields: handoffClearFields,
      })
      if (handoffError) throw handoffError
      if (targetStep) {
        for (const targetStudioNumber of targetStudioNumbers) {
          const { error: stageError } = await (supabase as any).rpc('set_studio_closure_preparation_stage', {
            p_studio_number: targetStudioNumber,
            p_stage: targetStep,
          })
          if (stageError) throw stageError
        }
      }
      setSuccess('Статус подготовки сохранён.')
      if (targetStep) setActiveFormStep(targetStep)
      await loadData()
      if (targetStep) setActiveFormStep(targetStep)
      setFormDirty(false)
      if (caseMode === 'create') {
        setActiveFormStep(targetStep ?? 'signal')
        setCaseMode('manage')
        setCaseEditorOpen(false)
        setSuccess(targetStep === 'termination'
          ? 'Экстренный кейс создан и доступен на этапах «Расторжение» и «Закрытие и база».'
          : targetStudioNumbers.length > 1
            ? `Создано кейсов: ${targetStudioNumbers.length}. Они добавлены на шаг 0.`
            : 'Новая запись добавлена на шаг 0 для дозаполнения.')
      }
      if (emergencySaveRef.current) {
        emergencySaveRef.current = false
        setEmergencyModalOpen(false)
      }
      if (closeAfterSaveRef.current) {
        closeAfterSaveRef.current = null
        setCaseEditorOpen(false)
        setDepartmentModalRow(null)
      }
    } catch (err) {
      closeAfterSaveRef.current = null
      emergencySaveRef.current = false
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  // Окончательное закрытие: цикл пройден, студия закрыта фактически.
  const completePreparation = async (row: ClosurePreparationRow) => {
    const regulation = regulationDetailsByStudio[row.studio_number] ?? {}
    const missing = missingForCompletion({
      ...row,
      legal_unilateral_sent_on: regulationDate(regulation, 'legal_unilateral_sent_on'),
      contract_expiry_on: regulationDate(regulation, 'contract_expiry_on'),
    })
    if (missing.length > 0) {
      setError(`Нельзя провести окончательное закрытие студии #${row.studio_number}: не хватает — ${missing.join('; ')}.`)
      return
    }
    if (!window.confirm(
      `Провести окончательное закрытие студии #${row.studio_number}?\n\n`
      + 'Все отметки собраны, студия закрыта фактически. Запись уйдёт в историю как завершённое закрытие.'
    )) return

    setEndingId(row.id)
    setError(null)
    setSuccess(null)
    try {
      const { error: endError } = await (supabase as any).rpc('complete_studio_closure_preparation', {
        p_studio_number: Number(row.studio_number),
        p_completed_on: todayIso(),
      })
      if (endError) throw endError
      setSuccess(`Закрытие студии #${row.studio_number} проведено окончательно.`)
      if (studioNumber === row.studio_number) resetForm()
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setEndingId(null)
    }
  }

  const finishCurrentCase = async () => {
    if (!selectedActiveRow || activeFormStep !== 'technical' || saving) return
    if (formDirty) {
      setError('Сначала сохраните изменения, затем завершайте закрытие кейса.')
      return
    }

    const missing = missingForCompletion({
      status_code: selectedActiveRow.status_code,
      owner_official_notice_received: noticeReceived,
      owner_official_notice_received_on: noticeReceivedOn || null,
      closure_confirmation_received: closureConfirmationReceived,
      closure_confirmation_received_on: closureConfirmationReceivedOn || null,
      termination_signed: terminationSigned,
      termination_signed_on: terminationSignedOn || null,
      legal_unilateral_sent_on: legalUnilateralSentOn || null,
      contract_expiry_on: contractExpiryOn || null,
    })
    if (missing.length > 0) {
      setError(`Нельзя завершить закрытие студии #${selectedActiveRow.studio_number}: не хватает — ${missing.join('; ')}.`)
      return
    }
    if (!window.confirm(`Завершить закрытие студии #${selectedActiveRow.studio_number}? Кейс уйдёт в историю завершённых.`)) return

    setEndingId(selectedActiveRow.id)
    setError(null)
    setSuccess(null)
    try {
      const { error: endError } = await (supabase as any).rpc('complete_studio_closure_preparation', {
        p_studio_number: Number(selectedActiveRow.studio_number),
        p_completed_on: todayIso(),
      })
      if (endError) throw endError
      setSuccess(`Закрытие студии #${selectedActiveRow.studio_number} завершено.`)
      resetForm()
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setEndingId(null)
    }
  }

  const sendStatusHandoffInformation = async () => {
    if (!selectedActiveRow || !statusHandoffTarget) return

    const isNoSales = statusHandoffTarget === 'NO_SALES'
    const eventType = isNoSales ? 'NO_SALES' : 'CLOSED'
    const targetStatus = isNoSales ? 'SERVICE' : 'CLOSED'
    const statusLabel = isNoSales ? 'Без продаж' : 'Закрыта'
    const generatedComment = buildClosureHandoffSummary(
      selectedActiveRow,
      statusHandoffTarget,
      statusHandoffClosureOn,
      statusHandoffClosureType,
    )
    if (!isNoSales && !statusHandoffClosureOn) {
      setError('Укажите дату фактического закрытия студии.')
      return
    }

    setStatusHandoffSubmitting(true)
    setError(null)
    try {
      await createOriginalChangeCase({
        eventType,
        changedAt: isNoSales ? todayIso() : statusHandoffClosureOn,
        targetStatus,
        studioInternalIds: [selectedActiveRow.studio_internal_id],
        comment: statusHandoffComment.trim() || null,
        payload: {
          happened: generatedComment,
          event_label: isNoSales ? 'Студия без продаж' : 'Студия закрыта',
          target_status: targetStatus,
          ...(isNoSales ? {} : { closure_type: statusHandoffClosureType }),
          source: 'studio_closure_preparation',
          closure_case_id: selectedActiveRow.id,
        },
      })
      const { error: handoffError } = await (supabase as any).rpc('update_studio_closure_status_handoffs', {
        p_studio_number: Number(selectedActiveRow.studio_number),
        p_no_sales_information_sent_on: isNoSales ? todayIso() : null,
        p_closed_information_sent_on: isNoSales ? null : todayIso(),
      })
      if (handoffError) throw handoffError

      if (isNoSales) setNoSalesInformationSentOn(todayIso())
      else setClosedInformationSentOn(todayIso())
      setStatusHandoffTarget(null)
      setStatusHandoffComment('')
      setSuccess(`Информация по студии #${selectedActiveRow.studio_number} передана в «Реестр студий» для перевода в статус «${statusLabel}».`)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setStatusHandoffSubmitting(false)
    }
  }

  const closeCaseEditor = () => {
    setCaseEditorOpen(false)
    setDepartmentModalRow(null)
  }

  const discardUnsavedChangesAndClose = () => {
    setFormDirty(false)
    if (unsavedCloseTarget === 'department') setDepartmentModalRow(null)
    else closeCaseEditor()
    setUnsavedCloseTarget(null)
  }

  const saveAndCloseWithUnsavedChanges = () => {
    if (!unsavedCloseTarget) return
    closeAfterSaveRef.current = unsavedCloseTarget === 'department' ? 'department' : 'case'
    setUnsavedCloseTarget(null)
    void save()
  }

  const hasUnsavedFormChanges = () => {
    if (!formDirty) return false
    const container = departmentModalRow ? departmentModalContentRef.current : caseEditorContentRef.current
    if (!container || initialFormSnapshotRef.current === null) return formDirty
    const currentSnapshot = JSON.stringify(
      Array.from(container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea'))
        .map(control => ({
          name: control.name,
          type: control instanceof HTMLInputElement ? control.type : '',
          value: control instanceof HTMLInputElement && control.type === 'checkbox' ? control.checked : control.value,
        }))
    )
    return currentSnapshot !== initialFormSnapshotRef.current
  }

  const requestCloseCaseEditor = () => {
    if (!hasUnsavedFormChanges()) {
      closeCaseEditor()
      return
    }
    setUnsavedCloseTarget('case')
  }

  const requestCloseDepartmentModal = () => {
    if (!hasUnsavedFormChanges()) {
      setDepartmentModalRow(null)
      return
    }
    setUnsavedCloseTarget('department')
  }

  const startEmergencyClosure = () => {
    if (!currentCaseStudio || saving) return
    const currentRegulation = regulationDetailsByStudio[currentCaseStudio.studio_number] ?? {}
    const alreadyOnTermination = selectedActiveRow?.current_stage === 'termination'
      && currentRegulation.case_scenario_status === 'CLOSURE_CONFIRMED'
    setEmergencyIntroVisible(!alreadyOnTermination)
    setEmergencyModalOpen(true)
  }

  const openEmergencyClosureForRow = (row: ClosurePreparationRow) => {
    if (saving) return
    fillFromRow(row, false)
    const regulation = regulationDetailsByStudio[row.studio_number] ?? {}
    setEmergencyIntroVisible(!(row.current_stage === 'termination' && regulation.case_scenario_status === 'CLOSURE_CONFIRMED'))
    setEmergencyModalOpen(true)
  }

  const openCancelDialog = (row: ClosurePreparationRow) => {
    setCancelTarget(row)
    setCancelReason('')
    setError(null)
    setSuccess(null)
  }

  // Отмена подготовки: работает на любой стадии, но требует причину — иначе это
  // был бы способ бесследно снять запись мимо гарда окончательного закрытия.
  const cancelPreparation = async () => {
    if (!cancelTarget) return
    const reason = cancelReason.trim()
    if (reason.length < 3) {
      setError('Укажите причину отмены подготовки.')
      return
    }

    setEndingId(cancelTarget.id)
    setError(null)
    setSuccess(null)
    try {
      const { error: cancelError } = await (supabase as any).rpc('cancel_studio_closure_preparation', {
        p_studio_number: Number(cancelTarget.studio_number),
        p_reason: reason,
        p_cancelled_on: todayIso(),
      })
      if (cancelError) throw cancelError
      setSuccess(`Кейс студии #${cancelTarget.studio_number} перенесён в архив.`)
      if (studioNumber === cancelTarget.studio_number) resetForm()
      setCancelTarget(null)
      setCancelReason('')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setEndingId(null)
    }
  }

  const activeFormStepIndex = CLOSURE_FORM_STEPS.findIndex(step => step.id === activeFormStep)
  const currentFormStep = caseMode === 'create'
    ? { id: 'signal' as ClosureFormStep, title: 'Новая запись', description: 'После сохранения кейс попадёт на шаг 0 для дозаполнения ответственными сотрудниками' }
    : CLOSURE_FORM_STEPS[activeFormStepIndex] ?? CLOSURE_FORM_STEPS[0]
  const previousFormStep = activeFormStepIndex > 0 ? CLOSURE_FORM_STEPS[activeFormStepIndex - 1] : undefined
  const nextFormStep = CLOSURE_FORM_STEPS[activeFormStepIndex + 1]
  const currentCaseStudio = selectedActiveRow ?? selectedStudioOption
  const isEmergencyCase = Boolean(
    currentCaseStudio
    && regulationDetailsByStudio[currentCaseStudio.studio_number]?.case_scenario_status === 'CLOSURE_CONFIRMED'
  )
  const activeDepartmentFields = {
    legal: {
      status: legalBlockStatus,
      setStatus: setLegalBlockStatus,
      dueOn: legalBlockDueOn,
      setDueOn: setLegalBlockDueOn,
      assignee: legalBlockAssignee,
      setAssignee: setLegalBlockAssignee,
      note: legalBlockNote,
      setNote: setLegalBlockNote,
    },
    finance: {
      status: financeBlockStatus,
      setStatus: setFinanceBlockStatus,
      dueOn: financeBlockDueOn,
      setDueOn: setFinanceBlockDueOn,
      assignee: financeBlockAssignee,
      setAssignee: setFinanceBlockAssignee,
      note: financeBlockNote,
      setNote: setFinanceBlockNote,
    },
    tech: {
      status: techBlockStatus,
      setStatus: setTechBlockStatus,
      dueOn: techBlockDueOn,
      setDueOn: setTechBlockDueOn,
      assignee: techBlockAssignee,
      setAssignee: setTechBlockAssignee,
      note: techBlockNote,
      setNote: setTechBlockNote,
    },
    pedagogy: {
      status: pedagogyBlockStatus,
      setStatus: setPedagogyBlockStatus,
      dueOn: pedagogyBlockDueOn,
      setDueOn: setPedagogyBlockDueOn,
      assignee: pedagogyBlockAssignee,
      setAssignee: setPedagogyBlockAssignee,
      note: pedagogyBlockNote,
      setNote: setPedagogyBlockNote,
    },
    sales: {
      status: salesBlockStatus,
      setStatus: setSalesBlockStatus,
      dueOn: salesBlockDueOn,
      setDueOn: setSalesBlockDueOn,
      assignee: salesBlockAssignee,
      setAssignee: setSalesBlockAssignee,
      note: salesBlockNote,
      setNote: setSalesBlockNote,
    },
  }[activeDepartmentBlock]
  const warningFlagsCount = [
    warningAccessLost,
    warningPartnerUnreachable,
    caseScenarioStatus === 'REBRANDING',
  ].filter(Boolean).length
  const warningPanel = (
    <div className="rounded-md border border-primary/30 bg-primary/5">
      <div
        className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left"
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="text-sm font-semibold">Тревожные признаки</span>
          <span className="truncate text-xs text-muted-foreground">
            {warningFlagsCount > 0 ? `Отмечено: ${warningFlagsCount}` : 'Не отмечены'}
          </span>
        </span>
      </div>
        <div className="border-t border-primary/20 px-3 py-3">
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={warningAccessLost} onChange={event => setWarningAccessLost(event.target.checked)} className="h-4 w-4" />
              У УК потерян доступ к соцсетям/системам
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={warningPartnerUnreachable} onChange={event => setWarningPartnerUnreachable(event.target.checked)} className="h-4 w-4" />
              Франчайзи не выходит на связь
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={caseScenarioStatus === 'REBRANDING'}
                onChange={event => {
                  if (event.target.checked) {
                    setCaseScenarioStatus('REBRANDING')
                    if (!caseScenarioChangedOn) setCaseScenarioChangedOn(todayIso())
                  } else if (caseScenarioStatus === 'REBRANDING') {
                    setCaseScenarioStatus('')
                    setCaseScenarioChangedOn('')
                    setCaseScenarioNote('')
                  }
                }}
                className="h-4 w-4"
              />
              Планируется ребрендинг
            </label>
          </div>
          <label className="mt-3 block space-y-1">
            <span className="text-sm font-medium">Ссылки на скрины / детали</span>
            <textarea
              value={warningNote}
              onChange={event => setWarningNote(event.target.value)}
              rows={2}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
    </div>
  )

  const emergencyClosureAction = (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-error/30 bg-error/5 p-3">
      {isEmergencyCase ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="inline-flex shrink-0 items-center gap-2 text-error"
          disabled={saving}
          onClick={() => void save(undefined, { status: null, changedOn: '', note: '' })}
        >
          Снять экстренность
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          className="inline-flex shrink-0 items-center gap-2 !bg-error !text-white hover:!bg-error/90"
          disabled={!currentCaseStudio || saving}
          onClick={startEmergencyClosure}
        >
          <AlertTriangle size={16} aria-hidden="true" />
          Экстренное закрытие
        </Button>
      )}
      <p className="min-w-[180px] flex-1 text-sm text-foreground">
        {isEmergencyCase ? 'Кейс отмечен как экстренный и доступен на двух финальных этапах.' : 'Используйте, когда студия уже фактически закрыта.'}
      </p>
    </div>
  )

  const clientLiabilityPanel = (
    <div className="rounded-md border border-border p-3">
      <h3 className="text-sm font-semibold">Клиентская кредиторка</h3>
      <div className="mt-1 text-xs text-muted-foreground">Ручной срез суммы, клиентов и источника расчёта.</div>
      {currentCaseStudio && (
        <div className="mt-3 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
          <div className="text-xs text-muted-foreground">Клиентов по последней дневной метрике</div>
          <div className="mt-0.5 font-medium">
            {currentCaseStudio.active_clients_count ?? '—'}
            {currentCaseStudio.active_clients_snapshot_on ? ` · срез ${currentCaseStudio.active_clients_snapshot_on}` : ''}
          </div>
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="block space-y-1">
          <span className="text-sm font-medium">Сумма</span>
          <input type="text" inputMode="decimal" value={clientLiabilityAmount} onChange={event => setClientLiabilityAmount(event.target.value)} placeholder="0" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Клиентов</span>
          <input type="text" inputMode="numeric" value={clientLiabilityCount} onChange={event => setClientLiabilityCount(event.target.value)} placeholder="0" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Дата среза</span>
          <input type="date" value={clientLiabilitySnapshotOn} onChange={event => setClientLiabilitySnapshotOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </label>
      </div>
      <label className="mt-3 block space-y-1">
        <span className="text-sm font-medium">Комментарий</span>
        <textarea value={clientLiabilityNote} onChange={event => setClientLiabilityNote(event.target.value)} rows={3} placeholder="Источник расчёта и способ закрытия обязательств" className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" />
      </label>
    </div>
  )

  return (
    <div className="space-y-4 pb-24">
      <button
        type="button"
        onClick={() => navigate('/management/hub')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Направления
      </button>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock size={22} className="text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">Кейсы закрытия и продажи студий</h1>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={startNewCase}>
          Новая запись
        </Button>
      </div>

      {(error || success) && (
        <div className={`rounded-md border px-3 py-2 text-sm ${
          error ? 'border-error/30 bg-error/10 text-error' : 'border-success/30 bg-success/10 text-success'
        }`}>
          {error ?? success}
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="flex min-w-[760px] gap-2">
          {CLOSURE_FORM_STEPS.map((step, index) => {
            const active = step.id === activeFormStep
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  setActiveFormStep(step.id)
                  setError(null)
                }}
                className={`relative flex h-[96px] flex-1 items-start gap-2 rounded-md border px-2.5 py-2 pr-9 text-left transition-colors ${
                  active
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {index}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-tight">{step.title.replace(/^\d+\.\s*/, '')}</span>
                  <span className="mt-0.5 block text-[11px] leading-tight">{step.description}</span>
                </span>
                <span className={`absolute right-2.5 top-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold tabular-nums ${
                  active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {stageCounts[step.id] ?? 0}
                </span>
                {(step.id === 'termination' || step.id === 'technical') && (emergencyStageCounts[step.id] ?? 0) > 0 && (
                  <span
                    title={`Экстренных кейсов: ${emergencyStageCounts[step.id]}`}
                    aria-label={`Экстренных кейсов: ${emergencyStageCounts[step.id]}`}
                    className="absolute right-8 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-error/60 text-xs font-bold text-error"
                  >
                    !
                  </span>
                )}
                {(warningStageCounts[step.id] ?? 0) > 0 && (
                  <span
                    title={`Кейсов с тревожными признаками: ${warningStageCounts[step.id]}`}
                    aria-label={`Кейсов с тревожными признаками: ${warningStageCounts[step.id]}`}
                    className={`absolute top-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-warning/70 text-xs font-bold text-warning ${
                      (step.id === 'termination' || step.id === 'technical') && (emergencyStageCounts[step.id] ?? 0) > 0 ? 'right-14' : 'right-8'
                    }`}
                  >
                    ⚠
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        <Card elevated={false} className="border border-border">
          <div className="text-xs text-muted-foreground">Ожидается</div>
          <div className="mt-1 text-2xl font-semibold">{stats.active}</div>
        </Card>
        <Card elevated={false} className="border border-border">
          <div className="text-xs text-muted-foreground">До 28 дней</div>
          <div className="mt-1 text-2xl font-semibold">{stats.dueSoon}</div>
        </Card>
        <Card elevated={false} className="border border-border">
          <div className="text-xs text-muted-foreground">Просрочено</div>
          <div className="mt-1 text-2xl font-semibold text-error">{stats.overdue}</div>
        </Card>
        <Card elevated={false} className="border border-border">
          <div className="text-xs text-muted-foreground">Без уведомления</div>
          <div className="mt-1 text-2xl font-semibold text-primary">{stats.missingNotice}</div>
        </Card>
        <Card elevated={false} className="border border-border">
          <div className="text-xs text-muted-foreground">Сроки блоков</div>
          <div className="mt-1 text-2xl font-semibold text-error">{stats.blockOverdue}</div>
        </Card>
      </div>

      {caseEditorOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onClick={requestCloseCaseEditor}
        >
          <div
            className="my-8 w-full max-w-2xl"
            onClick={event => event.stopPropagation()}
            onChange={() => setFormDirty(true)}
            ref={caseEditorContentRef}
          >
        <Card className="space-y-4">
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold">
                  {caseMode === 'create'
                    ? 'Создание кейса'
                    : selectedActiveRow ? `Ведение кейса #${selectedActiveRow.studio_number}` : 'Выберите кейс справа'}
                </h2>
                <div className="mt-1 text-xs text-muted-foreground">
                  {caseMode === 'create'
                    ? 'Любой сотрудник может создать кейс о закрытии, продаже или выходе из сети. Заполните поля, которые вы знаете.'
                    : 'Данные относятся только к выбранной строке из таблицы.'}
                </div>
              </div>
              <button
                type="button"
                onClick={requestCloseCaseEditor}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Закрыть"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
            <div className="text-sm font-semibold">{currentFormStep.title}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{currentFormStep.description}</div>
          </div>

          {activeFormStep !== 'signal' && currentCaseStudio && (
            <div className="rounded-md border border-border bg-card px-3 py-2">
              <div className="text-sm font-medium">Студия №{currentCaseStudio.studio_number}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {formatStudioMeta(currentCaseStudio) || 'Данные по студии не найдены'}
              </div>
            </div>
          )}

          {activeFormStep !== 'signal' && currentCaseStudio && (
            <div className="rounded-md border border-border/70 bg-muted/20">
              <button
                type="button"
                onClick={() => setCaseContextOpen(open => !open)}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left"
              >
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="text-sm font-semibold">Контекст кейса</span>
                  <span className="truncate text-xs text-muted-foreground">Сигнал, решение, долги и сроки</span>
                </span>
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-muted-foreground transition-transform ${caseContextOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {caseContextOpen && (
              <div className="border-t border-border/70 px-3 py-3">
              <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div><div className="text-xs text-muted-foreground">Сигнал</div><div className="truncate font-medium">{signalSource || 'Не указан'}</div></div>
                <div><div className="text-xs text-muted-foreground">Причина закрытия</div><div className="truncate font-medium">{closureReason || 'Не указана'}</div></div>
                <div><div className="text-xs text-muted-foreground">Дата закрытия</div><div className="font-medium">{expectedClosureOn || 'Не указана'}</div></div>
                <div><div className="text-xs text-muted-foreground">Решение по закрытию</div><div className="font-medium">{closureDecision || managementDecision || 'Не указано'}</div></div>
                <div><div className="text-xs text-muted-foreground">Помощь франчайзи</div><div className="font-medium">{partnerHelpDecision || managementOnlineHelpDecision || 'Не указана'}</div></div>
                <div><div className="text-xs text-muted-foreground">Официальное уведомление</div><div className="font-medium">{partnerMessageReceivedConfirmed ? 'Получено' : 'Не получено'}</div></div>
                <div><div className="text-xs text-muted-foreground">Изменение сценария</div><div className="truncate font-medium">{caseScenarioStatus ? CASE_SCENARIO_STATUS_LABELS[caseScenarioStatus] : 'Не зафиксировано'}</div></div>
                <div><div className="text-xs text-muted-foreground">Долг по роялти из системы</div><div className="font-medium">{formatRub(currentCaseStudio.royalty_debt_rub)}</div></div>
                <div><div className="text-xs text-muted-foreground">Долг по роялти от бухгалтерии</div><div className="font-medium">{financeRoyaltyDebtAccounting ? formatRub(Number(financeRoyaltyDebtAccounting.replace(',', '.'))) : 'Не указан'}</div></div>
                <div><div className="text-xs text-muted-foreground">Сверка с ЛМ</div><div className="truncate font-medium">{financeLmReconciliationNote || 'Не указана'}</div></div>
                <div><div className="text-xs text-muted-foreground">Ближайший срок</div><div className="font-medium">{legalReminderSentOn || legalPartnerResponseDueOn || legalDocumentsDueOn || legalSignDueOn || 'Не указан'}</div></div>
                <div><div className="text-xs text-muted-foreground">Информация от франчайзи</div><div className="font-medium">{partnerRequiredInfoReceived ? 'Получена' : 'Ожидается'}</div></div>
                <div><div className="text-xs text-muted-foreground">Путь расторжения</div><div className="font-medium">{legalTerminationPath || 'Не определён'}</div></div>
              </div>
              </div>
              )}
            </div>
          )}

          {warningPanel}

          {activeFormStep === 'signal' && (
            <>
          {selectedActiveRow ? (
            <div className="rounded-md border border-border bg-card px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Студия №{selectedActiveRow.studio_number}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatStudioMeta(selectedActiveRow) || 'Данные по студии не найдены'}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge variant="neutral" className="text-xs">{selectedActiveRow.status_code ?? 'Статус не указан'}</Badge>
                    <Badge variant={selectedActiveRow.case_kind === 'STUDIO_SALE' ? 'warning' : 'neutral'} className="text-xs">
                      {CASE_KIND_LABELS[selectedActiveRow.case_kind] ?? selectedActiveRow.case_kind}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <label className="block space-y-1">
              <span className="text-sm font-medium">Номер студии</span>
              <div className="relative" ref={selectorRef}>
                <Search size={16} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={studioQuery}
                  onFocus={() => setStudioSelectorOpen(true)}
                  onChange={event => {
                    setStudioQuery(event.target.value)
                    setStudioNumber('')
                    setStudioSelectorOpen(true)
                  }}
                  placeholder="Номер, город, владелец или адрес"
                  className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm"
                />
                <ChevronDown
                  size={16}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-transform ${studioSelectorOpen ? 'rotate-180' : ''}`}
                />

                {studioSelectorOpen && (
                  <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-md border border-border bg-card shadow-lg">
                    <div className="max-h-80 overflow-y-auto py-1">
                      {groupedStudioOptions.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          Студии не найдены
                        </div>
                      ) : (
                        groupedStudioOptions.map(([cityName, cityStudios], groupIndex) => (
                          <div key={cityName}>
                            {groupIndex > 0 && <div className="border-t border-border/70" />}
                            <div className="bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
                              {cityName}
                            </div>
                            {cityStudios.map(studio => {
                              const selected = studio.studio_number === studioNumber
                              return (
                                <button
                                  key={studio.studio_internal_id}
                                  type="button"
                                  onClick={() => selectStudio(studio)}
                                  className={`flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                                    selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                                  }`}
                                >
                                  <Building2 size={16} className={`mt-0.5 shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                                  <span className="min-w-0 flex-1">
                                    <span className={`block text-sm font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>
                                      №{studio.studio_number}
                                      {studio.address ? <span className="ml-2 font-normal text-muted-foreground">{studio.address}</span> : null}
                                      {studio.status_code ? <span className="ml-2 text-xs font-normal text-muted-foreground">{studio.status_code}</span> : null}
                                    </span>
                                    {formatStudioOptionDetails(studio) && (
                                      <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                                        <UserRound size={12} />
                                        <span className="truncate">{formatStudioOptionDetails(studio)}</span>
                                      </span>
                                    )}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedStudioOption && (
                <div className="text-xs text-muted-foreground">
                  {formatStudioLabel(selectedStudioOption)}
                </div>
              )}
            </label>
          )}

          {caseMode === 'create' && selectedStudioOption && relatedStudioOptions.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2">
              <div className="text-sm">
                <div className="font-medium">Добавить еще один кейс по этому франчайзи?</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Выберите другие студии владельца — для каждой будет создан отдельный кейс.
                </div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setRelatedStudiosOpen(true)}>
                {additionalStudioNumbers.length > 0 ? `Выбрано: ${additionalStudioNumbers.length}` : 'Выбрать'}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Тип кейса</span>
              <select
                value={caseKind}
                onChange={event => setCaseKind(event.target.value as ClosureCaseKind)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {CASE_KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Источник сигнала</span>
              <input
                type="text"
                value={signalSource}
                onChange={event => setSignalSource(event.target.value)}
                placeholder="Кто сообщил или где заметили"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
          </div>

          {caseMode === 'manage' && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Кейс-менеджер</span>
              <select
                value={caseManagerUserId}
                onChange={event => {
                  const selected = caseManagers.find(manager => manager.user_id === event.target.value)
                  setCaseManagerUserId(event.target.value)
                  setCaseManagerName(selected ? caseManagerLabel(selected) : '')
                }}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {!caseManagerUserId && <option value="" disabled>Кейс-менеджер не найден</option>}
                {caseManagers.map(manager => {
                  const name = caseManagerLabel(manager)
                  return <option key={manager.user_id} value={manager.user_id}>{name}{manager.tg_username ? ` · @${manager.tg_username}` : ''}</option>
                })}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Контактность франчайзи</span>
              <select
                value={partnerContactStatus}
                onChange={event => setPartnerContactStatus(event.target.value as PartnerContactStatus | '')}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Не указано</option>
                {CONTACT_STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          )}

          {caseMode === 'manage' && (<>
          <label className="block space-y-1">
            <span className="text-sm font-medium">Причина</span>
            <input
              type="text"
              value={closureReason}
              onChange={event => setClosureReason(event.target.value)}
              placeholder="Коротко: почему закрытие/продажа"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Резюме переговоров</span>
            <textarea
              value={negotiationSummary}
              onChange={event => setNegotiationSummary(event.target.value)}
              rows={3}
              placeholder="Что обсудили, что обещал франчайзи, следующий шаг"
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          </>)}

          {currentCaseStudio && (
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <h3 className="text-sm font-semibold">Данные из базы</h3>
              <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div>
                  <div className="text-xs text-muted-foreground">Дата начала владения</div>
                  <div className="mt-0.5 font-medium">{currentCaseStudio.ownership_started_on || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Другие активные студии франчайзи</div>
                  <div className="mt-0.5 font-medium">{currentCaseStudio.other_studios_summary || 'Нет / не найдены'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Договор</div>
                  <div className="mt-0.5 font-medium">
                    {currentCaseStudio.contract_number || '—'}
                    {currentCaseStudio.contract_legal_status_label ? ` · ${currentCaseStudio.contract_legal_status_label}` : ''}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Фактическое окончание договора</div>
                  <div className="mt-0.5 font-medium">{currentCaseStudio.contract_actual_end_date || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Долг по роялти</div>
                  <div className="mt-0.5 font-medium">{formatRub(currentCaseStudio.royalty_debt_rub)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Активные клиенты</div>
                  <div className="mt-0.5 font-medium">
                    {currentCaseStudio.active_clients_count ?? '—'}
                    {currentCaseStudio.active_clients_snapshot_on ? ` · срез ${currentCaseStudio.active_clients_snapshot_on}` : ''}
                  </div>
                </div>
              </div>
            </div>
          )}
          {caseMode === 'manage' && clientLiabilityPanel}
            </>
          )}

          {activeFormStep === 'decision' && (
            <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Статус введён с</span>
              <input
                type="date"
                value={preparationStartedOn}
                onChange={event => setPreparationStartedOn(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Ожидаемое закрытие</span>
              <input
                type="date"
                value={expectedClosureOn}
                onChange={event => setExpectedClosureOn(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="rounded-md border border-border p-3">
            <h3 className="text-sm font-semibold">Решение</h3>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium">Решение руководителя</span>
                <select
                  value={managementDecision}
                  onChange={event => setManagementDecision(event.target.value as ManagementDecision | '')}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Не указано</option>
                  {DECISION_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Дата решения</span>
                <input
                  type="date"
                  value={managementDecisionOn}
                  onChange={event => setManagementDecisionOn(event.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </div>
            <label className="mt-3 block space-y-1">
              <span className="text-sm font-medium">Подробные условия / комментарии</span>
              <textarea
                value={managementDecisionNote}
                onChange={event => setManagementDecisionNote(event.target.value)}
                rows={3}
                placeholder="Онлайн, отступные, попытка сохранения, исключения"
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium">Онлайн-помощь</span>
                <select
                  value={managementOnlineHelpDecision}
                  onChange={event => setManagementOnlineHelpDecision(event.target.value as OnlineHelpDecision | '')}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Не указано</option>
                  {ONLINE_HELP_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Помощь до</span>
                <input
                  type="date"
                  value={managementOnlineHelpUntil}
                  onChange={event => setManagementOnlineHelpUntil(event.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </div>
          </div>
            </>
          )}

          {activeFormStep === 'partner' && (
            <>
          <div className="space-y-5 rounded-md border border-border p-4">
            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">1. Получение уведомления</h3>
                <div className="mt-1 text-xs text-muted-foreground">Фиксируем официальное уведомление владельца.</div>
              </div>
              <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={noticeReceived}
                  onChange={event => {
                    const checked = event.target.checked
                    setNoticeReceived(checked)
                    if (checked && !noticeReceivedOn) setNoticeReceivedOn(todayIso())
                    if (!checked) setNoticeReceivedOn('')
                  }}
                  className="h-4 w-4"
                />
                Есть официальное уведомление от владельца
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Ссылка на уведомление в Яндекс Диске</span>
                <input
                  type="url"
                  value={partnerNoticeMissingReason}
                  disabled={!noticeReceived}
                  onChange={event => setPartnerNoticeMissingReason(event.target.value)}
                  placeholder="https://disk.yandex.ru/..."
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
                />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Дата уведомления</span>
                <input
                  type="date"
                  value={noticeReceivedOn}
                  disabled={!noticeReceived}
                  onChange={event => setNoticeReceivedOn(event.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
                />
              </label>
            </section>

            <section className="space-y-3 border-t border-border pt-5">
              <div>
                <h3 className="text-sm font-semibold">2. Отправка решения</h3>
                <div className="mt-1 text-xs text-muted-foreground">Фиксируем канал, адрес, срок ответа и подтверждения.</div>
              </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="block space-y-1">
                      <span className="text-sm font-medium">Дата отправки решения</span>
                      <input type="date" value={partnerMessageSentOn} onChange={event => setPartnerMessageSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-sm font-medium">Срок ответа</span>
                      <input type="date" value={partnerResponseDueOn} onChange={event => setPartnerResponseDueOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="block space-y-1">
                      <span className="text-sm font-medium">Канал</span>
                      <input type="text" value={partnerMessageChannel} onChange={event => setPartnerMessageChannel(event.target.value)} placeholder="Почта, чат ЛМ, Telegram" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                    <label className="block space-y-1">
                      <span className="text-sm font-medium">Адрес / чат</span>
                      <input type="text" value={partnerMessageAddress} onChange={event => setPartnerMessageAddress(event.target.value)} placeholder="Email, ссылка или название чата" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                  </div>
                  <div className="grid gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={partnerMessageSentConfirmed}
                        onChange={event => {
                          const checked = event.target.checked
                          setPartnerMessageSentConfirmed(checked)
                          setPartnerClosureTermsCommunicated(checked)
                          setPartnerCloseDateCommunicated(checked)
                          setPartnerActionsCommunicated(checked)
                          setPartnerCompletionTermsCommunicated(checked)
                        }}
                        className="h-4 w-4"
                      />
                      Отправка подтверждена
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={partnerMessageReceivedConfirmed} onChange={event => setPartnerMessageReceivedConfirmed(event.target.checked)} className="h-4 w-4" />
                      Получение франчайзи подтверждено
                    </label>
                  </div>
            </section>
          </div>
            </>
          )}

          {activeFormStep === 'departments' && (
            <>
          <div className="rounded-md border border-border p-3">
            <h3 className="text-sm font-semibold">Чек-листы отделов</h3>
            {[
              ['Юристы', legalBlockStatus, setLegalBlockStatus, legalBlockDueOn, setLegalBlockDueOn, legalBlockAssignee, setLegalBlockAssignee, legalBlockNote, setLegalBlockNote],
              ['Бухгалтерия', financeBlockStatus, setFinanceBlockStatus, financeBlockDueOn, setFinanceBlockDueOn, financeBlockAssignee, setFinanceBlockAssignee, financeBlockNote, setFinanceBlockNote],
              ['Техотдел', techBlockStatus, setTechBlockStatus, techBlockDueOn, setTechBlockDueOn, techBlockAssignee, setTechBlockAssignee, techBlockNote, setTechBlockNote],
              ['Педагоги/Академия', pedagogyBlockStatus, setPedagogyBlockStatus, pedagogyBlockDueOn, setPedagogyBlockDueOn, pedagogyBlockAssignee, setPedagogyBlockAssignee, pedagogyBlockNote, setPedagogyBlockNote],
              ['Продажи/открытия', salesBlockStatus, setSalesBlockStatus, salesBlockDueOn, setSalesBlockDueOn, salesBlockAssignee, setSalesBlockAssignee, salesBlockNote, setSalesBlockNote],
            ].map(([title, status, setStatus, dueOn, setDueOn, assignee, setAssignee, blockNote, setBlockNote]) => (
              <div key={title as string} className="mt-3 rounded-md border border-border/70 p-3">
                <div className="text-sm font-medium">{title as string}</div>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <select
                    value={status as WorkBlockStatus}
                    onChange={event => (setStatus as (value: WorkBlockStatus) => void)(event.target.value as WorkBlockStatus)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  >
                    {BLOCK_STATUS_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={dueOn as string}
                    onChange={event => (setDueOn as (value: string) => void)(event.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    value={assignee as string}
                    onChange={event => (setAssignee as (value: string) => void)(event.target.value)}
                    placeholder="Исполнитель"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <textarea
                  value={blockNote as string}
                  onChange={event => (setBlockNote as (value: string) => void)(event.target.value)}
                  rows={2}
                  placeholder="Результат, что проверено, что ждём"
                  className="mt-2 w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            ))}
          </div>
            </>
          )}

          {caseMode === 'manage' && activeFormStep === 'signal' && (
            <div className="rounded-md border border-border p-3">
              <h3 className="text-sm font-semibold">Дополнительные данные по студии</h3>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block space-y-1"><span className="text-sm font-medium">Количество залов</span><input type="number" min="0" value={hallsCount} onChange={event => setHallsCount(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                <label className="block space-y-1"><span className="text-sm font-medium">Другие долги</span><input type="text" inputMode="decimal" value={otherDebtAmount} onChange={event => setOtherDebtAmount(event.target.value)} placeholder="Сумма, кроме роялти" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block space-y-1"><span className="text-sm font-medium">Описание других долгов</span><textarea value={otherDebtNote} onChange={event => setOtherDebtNote(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                <label className="block space-y-1"><span className="text-sm font-medium">Договорённости с франчайзи</span><textarea value={partnerAgreementsSummary} onChange={event => setPartnerAgreementsSummary(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
              </div>
              <label className="mt-3 block space-y-1"><span className="text-sm font-medium">Иная важная информация</span><textarea value={importantInformation} onChange={event => setImportantInformation(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
            </div>
          )}

          {activeFormStep === 'partner' && (
            <div className="rounded-md border border-border p-3">
              <h3 className="text-sm font-semibold">Что передано франчайзи</h3>
              <div className="mt-1 text-xs text-muted-foreground">Пункты отмечаются автоматически после подтверждения отправки.</div>
              <div className="mt-3 grid gap-2 text-sm">
                <label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" checked={partnerClosureTermsCommunicated} disabled className="h-4 w-4" />Условия закрытия переданы</label>
                <label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" checked={partnerCloseDateCommunicated} disabled className="h-4 w-4" />Согласованная дата закрытия передана</label>
                <label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" checked={partnerActionsCommunicated} disabled className="h-4 w-4" />Действия франчайзи объяснены</label>
                <label className="flex items-center gap-2 text-muted-foreground"><input type="checkbox" checked={partnerCompletionTermsCommunicated} disabled className="h-4 w-4" />Условия завершения объяснены</label>
              </div>
            </div>
          )}

          {activeFormStep === 'legal' && (
            <div className="space-y-4">
              <section className="rounded-md border border-border p-3">
                <h3 className="text-sm font-semibold">Отправка регламента</h3>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(legalRegulationSentOn)}
                      onChange={event => setLegalRegulationSentOn(event.target.checked ? (legalRegulationSentOn || todayIso()) : '')}
                      className="h-4 w-4"
                    />
                    Отправлено на почту юристами
                  </label>
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Когда отправлен</span>
                    <input type="date" value={legalRegulationSentOn} disabled={!legalRegulationSentOn} onChange={event => setLegalRegulationSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50" />
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(legalLmChatNotifiedOn)}
                      onChange={event => setLegalLmChatNotifiedOn(event.target.checked ? (legalLmChatNotifiedOn || todayIso()) : '')}
                      className="h-4 w-4"
                    />
                    Продублировано в чат ЛМ
                  </label>
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Дата отправки</span>
                    <input type="date" value={legalLmChatNotifiedOn} disabled={!legalLmChatNotifiedOn} onChange={event => setLegalLmChatNotifiedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50" />
                  </label>
                </div>
              </section>

              <section className="rounded-md border border-border p-3">
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/70 bg-muted/20 p-3">
                <div className="text-sm"><div className="font-medium">Передача в «Без продаж»</div><div className="mt-0.5 text-muted-foreground">{noSalesInformationSentOn ? `Передано ${noSalesInformationSentOn}` : 'Ещё не передано'}</div></div>
                <Button type="button" size="sm" variant="outline" className="inline-flex items-center justify-center gap-1 whitespace-nowrap" disabled={!selectedActiveRow} onClick={() => { setStatusHandoffComment(''); setStatusHandoffTarget('NO_SALES') }}>
                  <Send size={15} aria-hidden="true" />
                  Передать информацию
                </Button>
              </div>
              </section>

              <section className="rounded-md border border-border p-3">
                <h3 className="text-sm font-semibold">Получение ответа от франчайзи</h3>
                <label className="mt-3 block space-y-1"><span className="text-sm font-medium">Получен ответ от франчайзи</span><input type="date" value={legalCorporateEmailSentOn} onChange={event => setLegalCorporateEmailSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                <div className="mt-3"><DeadlineSummary label="Ответ франчайзи на регламент (3 дня)" dueOn={addCalendarDays(legalRegulationSentOn, 3)} pendingText="Срок появится после заполнения даты отправления регламента" /></div>
              </section>
            </div>
          )}

          {activeFormStep === 'coordination' && (
            <div className="rounded-md border border-border p-3">
              <h3 className="text-sm font-semibold">Подразделения и педагогический контроль</h3>
              <div className="mt-3 grid gap-2 text-sm"><label className="flex items-center gap-2"><input type="checkbox" checked={pedagogyTrainingMonitoringContinued} onChange={event => setPedagogyTrainingMonitoringContinued(event.target.checked)} className="h-4 w-4" />Контроль обучения продолжается до даты закрытия</label><label className="flex items-center gap-2"><input type="checkbox" checked={pedagogySecretFeedbackSearchStopped} onChange={event => setPedagogySecretFeedbackSearchStopped(event.target.checked)} className="h-4 w-4" />Поиск тайных покупателей для обратной связи остановлен</label></div>
            </div>
          )}

          {activeFormStep === 'finance' && (
            <div className="space-y-4">
              <section className="rounded-md border border-border p-3">
                <h3 className="text-sm font-semibold">Бухгалтерия</h3>
                <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3 border-b border-border/70 pb-2"><span>Долг по роялти из системы:</span><strong>{formatRub(currentCaseStudio?.royalty_debt_rub)}</strong></div>
                <label className="flex items-center justify-between gap-3 border-b border-border/70 pb-2"><span>Долг по роялти от бухгалтерии:</span><input type="text" inputMode="decimal" value={financeRoyaltyDebtAccounting} onChange={event => setFinanceRoyaltyDebtAccounting(event.target.value)} placeholder="Сумма" className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                <label className="block space-y-1"><span>Сверка с ЛМ:</span><textarea value={financeLmReconciliationNote} onChange={event => setFinanceLmReconciliationNote(event.target.value)} rows={2} placeholder="Итог сверки, расхождения и принятое решение" className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={financeLmReconciled} onChange={event => setFinanceLmReconciled(event.target.checked)} className="h-4 w-4" />Данные сверены с ЛМ</label><label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={financeChiefAccountantConfirmed} onChange={event => setFinanceChiefAccountantConfirmed(event.target.checked)} className="h-4 w-4" />Результат зафиксирован главным бухгалтером</label>
              </section>
              <section className="rounded-md border border-border p-3">
                <h3 className="text-sm font-semibold">Юристы</h3>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="block space-y-1"><span className="text-sm font-medium">Напоминание франчайзи отправлено</span><input type="date" value={legalReminderSentOn} onChange={event => setLegalReminderSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                  <label className="block space-y-1"><span className="text-sm font-medium">Канал</span><input type="text" value={partnerMessageChannel} onChange={event => setPartnerMessageChannel(event.target.value)} placeholder="Почта, чат ЛМ, Telegram" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
                </div>
                <div className="mt-3"><DeadlineSummary label="Сбор документов и информации · 7 дней после перевода на этап" dueOn={addCalendarDays(legalReminderSentOn, 7)} completed={partnerRequiredInfoReceived} pendingText={null} /></div>
              </section>
            </div>
          )}

          {activeFormStep === 'termination' && (
            <div className="space-y-4">
              <section className="rounded-md border border-border">
                <button type="button" onClick={() => setBilateralTerminationOpen(open => !open)} className="flex w-full items-center justify-between gap-3 p-3 text-left"><span className="text-sm font-semibold">Двустороннее расторжение</span><ChevronDown size={18} className={bilateralTerminationOpen ? 'transition-transform' : '-rotate-90 transition-transform'} /></button>
                {bilateralTerminationOpen && <div className="border-t border-border p-3">
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">{[['Подготовлено',legalTerminationPreparedOn,setLegalTerminationPreparedOn],['Отправлено',legalTerminationSentOn,setLegalTerminationSentOn]].map(([label,value,setter]) => <label key={label as string} className="block space-y-1"><span className="text-sm font-medium">{label as string}</span><input type="date" value={value as string} onChange={event => (setter as (value: string) => void)(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>)}</div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-md border border-border/70 p-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={closureConfirmationReceived}
                      onChange={event => {
                        const checked = event.target.checked
                        setClosureConfirmationReceived(checked)
                        if (checked && !closureConfirmationReceivedOn) setClosureConfirmationReceivedOn(todayIso())
                        if (!checked) setClosureConfirmationReceivedOn('')
                      }}
                      className="h-4 w-4"
                    />
                    Получено подтверждение о закрытии
                  </label>
                  <label className="mt-2 block space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Дата подтверждения</span>
                    <input type="date" value={closureConfirmationReceivedOn} disabled={!closureConfirmationReceived} onChange={event => setClosureConfirmationReceivedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50" />
                  </label>
                </div>
                <div className="rounded-md border border-border/70 p-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={terminationSigned}
                      onChange={event => {
                        const checked = event.target.checked
                        setTerminationSigned(checked)
                        if (checked && !terminationSignedOn) setTerminationSignedOn(todayIso())
                        if (!checked) setTerminationSignedOn('')
                      }}
                      className="h-4 w-4"
                    />
                    Подписано расторжение
                  </label>
                  <label className="mt-2 block space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Дата расторжения</span>
                    <input type="date" value={terminationSignedOn} disabled={!terminationSigned} onChange={event => setTerminationSignedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50" />
                  </label>
                </div>
              </div>
                    <div className="mt-3"><DeadlineSummary label="Срок — один день после направления" dueOn={addCalendarDays(legalTerminationSentOn, 1)} completed={terminationSigned} pendingText={null} /></div>
                </div>}
              </section>
              <section className="rounded-md border border-border">
                <button type="button" onClick={() => setUnilateralTerminationOpen(open => !open)} className="flex w-full items-center justify-between gap-3 p-3 text-left"><span className="text-sm font-semibold">Одностороннее расторжение</span><ChevronDown size={18} className={unilateralTerminationOpen ? 'transition-transform' : '-rotate-90 transition-transform'} /></button>
                {unilateralTerminationOpen && <div className="border-t border-border p-3"><label className="block space-y-1"><span className="text-sm font-medium">Дата отправки одностороннего расторжения</span><input type="date" value={legalUnilateralSentOn} onChange={event => setLegalUnilateralSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label></div>}
              </section>
              <section className="rounded-md border border-border">
                <button type="button" onClick={() => setContractExpiryTerminationOpen(open => !open)} className="flex w-full items-center justify-between gap-3 p-3 text-left"><span className="text-sm font-semibold">Расторжение по окончанию ДКК</span><ChevronDown size={18} className={contractExpiryTerminationOpen ? 'transition-transform' : '-rotate-90 transition-transform'} /></button>
                {contractExpiryTerminationOpen && (
                  <div className="grid grid-cols-1 gap-3 border-t border-border p-3 md:grid-cols-2">
                    <label className="block space-y-1">
                      <span className="text-sm font-medium">Дата окончания ДКК</span>
                      <input type="date" value={contractExpiryOn} onChange={event => setContractExpiryOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                    <div className="rounded-md border border-border/70 p-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={closureConfirmationReceived}
                          onChange={event => {
                            const checked = event.target.checked
                            setClosureConfirmationReceived(checked)
                            if (checked && !closureConfirmationReceivedOn) setClosureConfirmationReceivedOn(todayIso())
                            if (!checked) setClosureConfirmationReceivedOn('')
                          }}
                          className="h-4 w-4"
                        />
                        Получено подтверждение о закрытии
                      </label>
                      <label className="mt-2 block space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">Дата подтверждения</span>
                        <input type="date" value={closureConfirmationReceivedOn} disabled={!closureConfirmationReceived} onChange={event => setClosureConfirmationReceivedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50" />
                      </label>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}

          {activeFormStep === 'technical' && (
            <div className="space-y-3">
              <section className="rounded-md border border-border">
                <button type="button" onClick={() => setTechChecklistOpen(open => !open)} className="flex w-full items-center justify-between gap-3 p-3 text-left"><span className="flex items-center gap-2 text-sm font-semibold">Техническое закрытие<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{countTechClosureItems(technicalChecklist)}/{TECH_CLOSURE_ITEMS.length}</span></span><ChevronDown size={18} className={techChecklistOpen ? 'transition-transform' : '-rotate-90 transition-transform'} /></button>
                {techChecklistOpen && <div className="space-y-2 border-t border-border p-3 text-sm">{TECH_CLOSURE_ITEMS.map(item => <label key={item.id} className="flex items-start gap-2"><input type="checkbox" checked={Boolean(technicalChecklist[item.id])} onChange={event => { setFormDirty(true); setTechnicalChecklist(current => ({ ...current, [item.id]: event.target.checked })) }} className="mt-0.5 h-4 w-4" /><span>{item.label}</span></label>)}<label className="block space-y-1 pt-2"><span className="text-sm font-medium">Техотдел сообщил юристам</span><input type="date" value={techLegalNotifiedOn} onChange={event => { setFormDirty(true); setTechLegalNotifiedOn(event.target.value) }} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" /></label></div>}
              </section>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/70 bg-muted/20 p-3"><div className="text-sm"><div className="font-medium">Передача в «Закрыта»</div><div className="mt-0.5 text-muted-foreground">{closedInformationSentOn ? `Дата передачи: ${closedInformationSentOn}` : 'Ещё не передано'}</div></div><Button type="button" size="sm" variant="outline" className="inline-flex items-center justify-center gap-1 whitespace-nowrap" disabled={!selectedActiveRow} onClick={() => { setStatusHandoffComment(''); setStatusHandoffClosureOn(selectedActiveRow?.expected_closure_on ?? ''); setStatusHandoffClosureType('STANDARD'); setStatusHandoffTarget('CLOSED') }}><Send size={15} aria-hidden="true" />Передать информацию</Button></div>
            </div>
          )}

          {relatedStudiosOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setRelatedStudiosOpen(false)}>
              <Card className="w-full max-w-lg space-y-4" onClick={event => event.stopPropagation()}>
                <div>
                  <h3 className="text-base font-semibold">Добавить еще один кейс по этому франчайзи?</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Выберите студии. По каждой создастся отдельный кейс с теми же исходными данными.</p>
                </div>
                <div className="max-h-72 space-y-2 overflow-y-auto">
                  {relatedStudioOptions.map(studio => {
                    const selected = additionalStudioNumbers.includes(studio.studio_number)
                    return (
                      <label key={studio.studio_internal_id} className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2 hover:bg-muted/40">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => setAdditionalStudioNumbers(current => (
                            selected
                              ? current.filter(number => number !== studio.studio_number)
                              : [...current, studio.studio_number]
                          ))}
                          className="mt-0.5 h-4 w-4"
                        />
                        <span className="min-w-0 text-sm">
                          <span className="font-medium">Студия №{studio.studio_number}</span>
                          <span className="ml-2 text-muted-foreground">{formatStudioMeta(studio) || studio.address || 'Адрес не указан'}</span>
                        </span>
                      </label>
                    )
                  })}
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setRelatedStudiosOpen(false)}>Отмена</Button>
                  <Button type="button" size="sm" onClick={() => setRelatedStudiosOpen(false)}>Готово</Button>
                </div>
              </Card>
            </div>
          )}

          {caseMode === 'manage' && formWarnings.length > 0 && (
            <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
              <div className="flex items-center gap-2 font-medium text-primary">
                <AlertTriangle size={15} />
                Проверьте данные перед сохранением
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {formWarnings.map(warning => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <label className="block space-y-1">
            <span className="text-sm font-medium">Комментарий</span>
            <textarea
              value={note}
              onChange={event => setNote(event.target.value)}
              rows={4}
              placeholder="Что вы еще знаете?"
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <div className="flex flex-col gap-3 border-t border-border pt-3">
            {emergencyClosureAction}
            {caseMode === 'manage' && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => previousFormStep && void save(previousFormStep.id)}
                disabled={!previousFormStep || saving}
              >
                Вернуть на предыдущий этап
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => nextFormStep && void save(nextFormStep.id)}
                disabled={!nextFormStep || saving}
              >
                Передать на следующий этап
              </Button>
            </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              {caseMode === 'manage' && selectedActiveRow && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-error"
                  onClick={() => openCancelDialog(selectedActiveRow)}
                  disabled={saving}
                >
                  Перенести в архив
                </Button>
              )}
              <div className="ml-auto flex flex-wrap justify-end gap-2">
                {activeFormStep === 'technical' && caseMode === 'manage' && selectedActiveRow && (
                  <Button size="sm" variant="outline" className="text-success" onClick={() => void finishCurrentCase()} disabled={saving || loading || endingId === selectedActiveRow.id}>
                    Завершить закрытие
                  </Button>
                )}
                <Button size="sm" className="inline-flex items-center justify-center gap-1 whitespace-nowrap" onClick={() => void save()} disabled={saving || loading}>
                  <CheckCircle2 size={16} className="mr-1" />
                  {saving ? 'Сохраняю...' : 'Сохранить'}
                </Button>
                <Button size="sm" variant="outline" onClick={requestCloseCaseEditor} disabled={saving}>
                  Закрыть
                </Button>
              </div>
            </div>
          </div>
        </Card>
          </div>
        </div>
      )}

      <Card className="space-y-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-semibold">{STAGE_TABLE_TITLES[activeFormStep]}</h2>
              <div className="mt-1 text-xs text-muted-foreground">
                {STAGE_TABLE_DESCRIPTIONS[activeFormStep]}
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <select
                value={quickFilter}
                onChange={event => setQuickFilter(event.target.value as typeof quickFilter)}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="all">Все активные</option>
                <option value="overdue">Просрочено</option>
                <option value="waiting_partner">Ждём франчайзи</option>
                <option value="sale">Продажи</option>
                <option value="warnings">С тревожными признаками</option>
              </select>
              <div className="relative w-full md:w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Студия, город, владелец, адрес"
                  className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Загрузка...</div>
          ) : filteredActiveRows.length === 0 ? (
            <div className="rounded-md border border-border py-8 text-center text-sm text-muted-foreground">
              Активных подготовок к закрытию не найдено.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1180px] table-fixed text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="w-[260px] py-2 pl-3 pr-4 font-medium">Студия</th>
                    {activeFormStep === 'signal' && (
                      <>
                        <th className="w-[190px] py-2 pr-4 font-medium">Кейс</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Сигнал</th>
                        <th className="w-[260px] py-2 pr-4 font-medium">Вводные</th>
                        <th className="w-[220px] py-2 pr-4 font-medium">Комментарий</th>
                      </>
                    )}
                    {activeFormStep === 'decision' && (
                      <>
                        <th className="w-[220px] py-2 pr-4 font-medium">Решение</th>
                        <th className="w-[180px] py-2 pr-4 font-medium">Срок закрытия</th>
                        <th className="w-[260px] py-2 pr-4 font-medium">Условия</th>
                        <th className="w-[220px] py-2 pr-4 font-medium">Комментарий</th>
                      </>
                    )}
                    {activeFormStep === 'partner' && (
                      <>
                        <th className="w-[230px] py-2 pr-4 font-medium">Отправка</th>
                        <th className="w-[230px] py-2 pr-4 font-medium">Уведомление</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">Обязательства перед клиентами</th>
                        <th className="w-[220px] py-2 pr-4 font-medium">Комментарий</th>
                      </>
                    )}
                    {activeFormStep === 'departments' && (
                      <>
                        <th className="w-[320px] py-2 pr-4 font-medium">Блоки отделов</th>
                        <th className="w-[230px] py-2 pr-4 font-medium">Тревожные признаки</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">После закрытия</th>
                        <th className="w-[220px] py-2 pr-4 font-medium">Комментарий</th>
                      </>
                    )}
                    {activeFormStep === 'legal' && (
                      <>
                        <th className="w-[220px] py-2 pr-4 font-medium">Регламент и статус</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">Корпоративная почта</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">Чат ЛМ</th>
                        <th className="w-[220px] py-2 pr-4 font-medium">Контрольный срок</th>
                      </>
                    )}
                    {activeFormStep === 'coordination' && (
                      <>
                        <th className="w-[210px] py-2 pr-4 font-medium">Передача статуса</th>
                        <th className="w-[240px] py-2 pr-4 font-medium">Педагогический контроль</th>
                        <th className="w-[230px] py-2 pr-4 font-medium">Фактическая ситуация</th>
                        <th className="w-[180px] py-2 pr-4 font-medium">Дата фиксации</th>
                      </>
                    )}
                    {activeFormStep === 'finance' && (
                      <>
                        <th className="w-[210px] py-2 pr-4 font-medium">Напоминание и срок</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">Долг из системы</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Долг бухгалтерии</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Сверка</th>
                      </>
                    )}
                    {activeFormStep === 'termination' && (
                      <>
                        <th className="w-[210px] py-2 pr-4 font-medium">Информация франчайзи</th>
                        <th className="w-[200px] py-2 pr-4 font-medium">Маршрут</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Документы</th>
                        <th className="w-[190px] py-2 pr-4 font-medium">Подпись</th>
                      </>
                    )}
                    {activeFormStep === 'technical' && (
                      <>
                        <th className="w-[230px] py-2 pr-4 font-medium">Техническое закрытие</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Сообщение юристам</th>
                        <th className="w-[210px] py-2 pr-4 font-medium">Передача в «Закрыта»</th>
                        <th className="w-[180px] py-2 pr-4 font-medium">Статус</th>
                      </>
                    )}
                    <th className="w-[170px] py-2 pr-3 text-right font-medium">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActiveRows.map(row => {
                    const days = daysUntil(row.expected_closure_on)
                    const isOverdue = days !== null && days < 0
                    const isNear = days !== null && days >= 0 && days <= 14
                    const regulation = regulationDetailsByStudio[row.studio_number] ?? {}
                    const missing = missingForCompletion({
                      ...row,
                      legal_unilateral_sent_on: regulationDate(regulation, 'legal_unilateral_sent_on'),
                      contract_expiry_on: regulationDate(regulation, 'contract_expiry_on'),
                    })
                    const canComplete = missing.length === 0
                    const isEmergencyCase = regulation.case_scenario_status === 'CLOSURE_CONFIRMED'
                    const isRebrandingCase = regulation.case_scenario_status === 'REBRANDING'
                    const warningLabels = [
                      ...warningLabelsForRow(row),
                      ...(isRebrandingCase ? ['Планируется ребрендинг'] : []),
                    ]
                    const hasWarningSigns = warningLabels.length > 0
                    return (
                      <tr
                        key={row.id}
                        className={`border-b border-border/70 align-top ${
                          selectedActiveRow?.id === row.id ? 'bg-primary/5' : ''
                        } ${isEmergencyCase ? 'border-l-2 border-l-error' : hasWarningSigns ? 'border-l-2 border-l-warning' : ''}`}
                      >
                        <td className="py-3 pl-3 pr-4">
                          <div className="text-left font-medium text-foreground">
                            {(hasWarningSigns || isEmergencyCase) && (
                              <span
                                title={isEmergencyCase ? 'Экстренный кейс' : warningLabels.join(' · ')}
                                aria-label={isEmergencyCase ? 'Экстренный кейс' : warningLabels.join(', ')}
                                className={`mr-1 inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs font-bold ${
                                  isEmergencyCase ? 'border-error/60 text-error' : 'border-warning/70 text-warning'
                                }`}
                              >
                                {isEmergencyCase ? '!' : '⚠'}
                              </span>
                            )}
                            {row.city_name && (
                              <span>{row.city_name}</span>
                            )}
                            {row.city_name && <span className="mx-1 text-muted-foreground">·</span>}
                            <span className="text-primary">#{row.studio_number}</span>
                            {row.address && (
                              <span className="ml-1 font-normal text-muted-foreground">· {row.address}</span>
                            )}
                          </div>
                          {formatOwnerLabel(row) && (
                            <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                              <UserRound size={12} />
                              <span className="truncate">{formatOwnerLabel(row)}</span>
                            </div>
                          )}
                        </td>
                        {activeFormStep === 'signal' && (
                          <>
                            <td className="py-3 pr-4">
                            <Badge variant={row.case_kind === 'STUDIO_SALE' ? 'warning' : 'neutral'} className="text-xs">
                              {CASE_KIND_LABELS[row.case_kind] ?? row.case_kind}
                            </Badge>
                            {isEmergencyCase && (
                              <span className="ml-1 inline-flex items-center gap-1 rounded border border-error/60 px-1.5 py-0.5 text-xs font-semibold text-error" title="Экстренный кейс">
                                <AlertTriangle size={12} aria-hidden="true" />
                                Экстренный
                              </span>
                            )}
                              <div className="mt-1 max-w-[180px] truncate text-xs text-muted-foreground">
                                {row.case_manager_name || 'Кейс-менеджер не указан'}
                              </div>
                              <div className="mt-0.5 text-xs text-muted-foreground">Статус: {row.status_code ?? '—'}</div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[220px] text-xs">
                                <div className="font-medium text-foreground">{row.signal_source || 'Источник не указан'}</div>
                                <div className="mt-1 text-muted-foreground">Введён: {row.preparation_started_on}</div>
                                <div className="mt-0.5 text-muted-foreground">Добавил: {row.created_by_name?.trim() || '—'}</div>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[260px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.closure_reason || 'Причина не указана'}
                                {row.negotiation_summary ? `\n${row.negotiation_summary}` : ''}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[220px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.note || '—'}
                              </div>
                            </td>
                          </>
                        )}
                        {activeFormStep === 'decision' && (
                          <>
                            <td className="py-3 pr-4">
                              {row.management_decision ? (
                                <div>
                                  <Badge variant="neutral" className="text-xs">
                                    {DECISION_LABELS[row.management_decision] ?? row.management_decision}
                                  </Badge>
                                  {row.management_decision_on && (
                                    <div className="mt-1 text-xs text-muted-foreground">{row.management_decision_on}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Решение не указано</span>
                              )}
                            </td>
                            <td className="py-3 pr-4 whitespace-nowrap">
                              {row.expected_closure_on ? (
                                <div>
                                  <div>{row.expected_closure_on}</div>
                                  <div className={`mt-0.5 flex items-center gap-1 text-xs ${
                                    isOverdue ? 'text-error' : isNear ? 'text-primary' : 'text-muted-foreground'
                                  }`}>
                                    {isOverdue || isNear ? <AlertTriangle size={13} /> : null}
                                    {days === null ? '' : days < 0 ? `${Math.abs(days)} дн. просрочки` : `${days} дн.`}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Не указано</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[260px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.management_decision_note || 'Условия не указаны'}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[220px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.note || '—'}
                              </div>
                            </td>
                          </>
                        )}
                        {activeFormStep === 'partner' && (
                          <>
                            <td className="py-3 pr-4">
                              <div className="space-y-1 text-xs">
                                <div>{row.partner_message_sent_on || 'Дата отправки не указана'}</div>
                                <div className="text-muted-foreground">{row.partner_message_channel || 'Канал не указан'}</div>
                                <div className="max-w-[220px] truncate text-muted-foreground">{row.partner_message_address || 'Адрес/чат не указан'}</div>
                                {row.partner_response_due_on && (
                                  <div className={blockDueOverdue('IN_PROGRESS', row.partner_response_due_on) ? 'text-error' : 'text-muted-foreground'}>
                                    Ответ до {row.partner_response_due_on}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="space-y-1 text-xs">
                                <div className={row.partner_message_sent_confirmed ? 'text-success' : 'text-muted-foreground'}>
                                  {row.partner_message_sent_confirmed ? 'Отправка подтверждена' : 'Отправка не подтверждена'}
                                </div>
                                <div className={row.partner_message_received_confirmed ? 'text-success' : 'text-muted-foreground'}>
                                  {row.partner_message_received_confirmed ? 'Получение подтверждено' : 'Получение не подтверждено'}
                                </div>
                                <div className={row.owner_official_notice_received ? 'text-success' : 'text-muted-foreground'}>
                                  {row.owner_official_notice_received ? `Уведомление: ${row.owner_official_notice_received_on || 'дата не указана'}` : 'Официального уведомления нет'}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="space-y-1 text-xs text-muted-foreground">
                                <div>Сумма: {row.client_liability_amount == null ? '—' : row.client_liability_amount}</div>
                                <div>Клиентов: {row.client_liability_count == null ? '—' : row.client_liability_count}</div>
                                <div>Срез: {row.client_liability_snapshot_on || '—'}</div>
                                {row.client_liability_note && (
                                  <div className="max-w-[220px] whitespace-pre-wrap">{row.client_liability_note}</div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[220px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.partner_notice_missing_reason || row.note || '—'}
                              </div>
                            </td>
                          </>
                        )}
                        {activeFormStep === 'departments' && (
                          <>
                            <td className="py-3 pr-4">
                              <div className="grid gap-1 text-xs">
                                {[
                                  ['Юр', row.legal_block_status, row.legal_block_due_on, row.legal_block_assignee],
                                  ['Бух', row.finance_block_status, row.finance_block_due_on, row.finance_block_assignee],
                                  ['Тех', row.tech_block_status, row.tech_block_due_on, row.tech_block_assignee],
                                  ['Пед', row.pedagogy_block_status, row.pedagogy_block_due_on, row.pedagogy_block_assignee],
                                  ['Прод', row.sales_block_status, row.sales_block_due_on, row.sales_block_assignee],
                                ].map(([label, status, dueOn, assignee]) => (
                                  <div key={label} className={blockDueOverdue(status as WorkBlockStatus, dueOn as string | null) ? 'text-error' : 'text-muted-foreground'}>
                                    <span className="font-medium text-foreground">{label}</span>: {BLOCK_STATUS_LABELS[status as WorkBlockStatus] ?? status}
                                    {dueOn ? ` · ${dueOn}` : ''}
                                    {assignee ? ` · ${assignee}` : ''}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[240px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {warningLabels.join(', ') || 'Не отмечены'}
                                {row.warning_note ? `\n${row.warning_note}` : ''}
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="space-y-1 text-xs">
                                <div className={row.closure_confirmation_received ? 'text-success' : 'text-muted-foreground'}>
                                  <span className="inline-flex items-center gap-1">
                                    {row.closure_confirmation_received ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                                    Подтверждение
                                  </span>
                                  {row.closure_confirmation_received_on && (
                                    <span className="ml-1 text-muted-foreground">{row.closure_confirmation_received_on}</span>
                                  )}
                                </div>
                                <div className={row.termination_signed ? 'text-success' : 'text-muted-foreground'}>
                                  <span className="inline-flex items-center gap-1">
                                    {row.termination_signed ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                                    Расторжение
                                  </span>
                                  {row.termination_signed_on && (
                                    <span className="ml-1 text-muted-foreground">{row.termination_signed_on}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[220px] whitespace-pre-wrap text-xs text-muted-foreground">
                                {row.legal_block_note || row.finance_block_note || row.tech_block_note || row.pedagogy_block_note || row.sales_block_note || row.note || '—'}
                              </div>
                            </td>
                          </>
                        )}
                        {activeFormStep === 'legal' && (
                          <>
                            <td className="py-3 pr-4"><div className="space-y-1 text-xs"><div className={row.legal_regulation_sent_on ? 'text-success' : 'text-muted-foreground'}>{row.legal_regulation_sent_on ? `Регламент: ${row.legal_regulation_sent_on}` : 'Регламент не отправлен'}</div><div className={regulationDate(regulation, 'status_no_sales_information_sent_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'status_no_sales_information_sent_on') ? `В «Без продаж»: ${regulationDate(regulation, 'status_no_sales_information_sent_on')}` : 'В «Без продаж» не передано'}</div></div></td>
                            <td className="py-3 pr-4 text-xs"><span className={regulationDate(regulation, 'legal_corporate_email_sent_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'legal_corporate_email_sent_on') || 'Не направлено'}</span></td>
                            <td className="py-3 pr-4 text-xs"><span className={regulationDate(regulation, 'legal_lm_chat_notified_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'legal_lm_chat_notified_on') || 'Не продублировано'}</span></td>
                            <td className="py-3 pr-4 text-xs"><div className={blockDueOverdue('IN_PROGRESS', row.legal_partner_response_due_on) ? 'text-error' : 'text-muted-foreground'}>Ответ франчайзи: {row.legal_partner_response_due_on || 'не указан'}</div><div className="mt-1 text-muted-foreground">Напоминание: {regulationDate(regulation, 'legal_reminder_due_on') || 'не рассчитано'}</div></td>
                          </>
                        )}
                        {activeFormStep === 'coordination' && (
                          <>
                            <td className="py-3 pr-4 text-xs"><div className="space-y-1"><div className={regulationDate(regulation, 'status_no_sales_information_sent_on') ? 'text-success' : 'text-muted-foreground'}>В «Без продаж»: {regulationDate(regulation, 'status_no_sales_information_sent_on') || 'не передано'}</div><div className={regulationDate(regulation, 'status_closed_information_sent_on') ? 'text-success' : 'text-muted-foreground'}>В «Закрыта»: {regulationDate(regulation, 'status_closed_information_sent_on') || 'не передано'}</div></div></td>
                            <td className="py-3 pr-4"><div className="space-y-1 text-xs"><div className={Boolean(regulation.pedagogy_training_monitoring_continued) ? 'text-success' : 'text-muted-foreground'}>{regulation.pedagogy_training_monitoring_continued ? 'Контроль обучения продолжается' : 'Контроль не отмечен'}</div><div className={Boolean(regulation.pedagogy_secret_feedback_search_stopped) ? 'text-success' : 'text-muted-foreground'}>{regulation.pedagogy_secret_feedback_search_stopped ? 'Поиск обратной связи остановлен' : 'Поиск не остановлен'}</div></div></td>
                            <td className="py-3 pr-4 text-xs"><span className="font-medium text-foreground">{CASE_SCENARIO_STATUS_LABELS[regulation.case_scenario_status as CaseScenarioStatus] || 'Не зафиксирована'}</span></td>
                            <td className="py-3 pr-4 text-xs text-muted-foreground">{regulationDate(regulation, 'case_scenario_changed_on') || '—'}</td>
                          </>
                        )}
                        {activeFormStep === 'finance' && (
                          <>
                            <td className="py-3 pr-4 text-xs"><div className={row.legal_reminder_sent_on ? 'text-success' : 'text-muted-foreground'}>Напоминание: {row.legal_reminder_sent_on || 'не отправлено'}</div><div className={`mt-1 ${blockDueOverdue('IN_PROGRESS', row.legal_documents_due_on) ? 'text-error' : 'text-muted-foreground'}`}>Документы до: {row.legal_documents_due_on || 'не указан'}</div></td>
                            <td className="py-3 pr-4 text-xs font-medium">{formatRub(typeof regulation.finance_royalty_debt_system === 'number' ? regulation.finance_royalty_debt_system : row.royalty_debt_rub)}</td>
                            <td className="py-3 pr-4 text-xs font-medium">{formatRub(typeof regulation.finance_royalty_debt_accounting === 'number' ? regulation.finance_royalty_debt_accounting : null)}</td>
                            <td className="py-3 pr-4"><div className="space-y-1 text-xs"><div className={Boolean(regulation.finance_lm_reconciled) ? 'text-success' : 'text-muted-foreground'}>{regulation.finance_lm_reconciled ? 'Сверено с ЛМ' : 'Сверка с ЛМ не завершена'}</div><div className={Boolean(regulation.finance_chief_accountant_confirmed) ? 'text-success' : 'text-muted-foreground'}>{regulation.finance_chief_accountant_confirmed ? 'Подтверждено главбухом' : 'Ожидается главбух'}</div></div></td>
                          </>
                        )}
                        {activeFormStep === 'termination' && (
                          <>
                            <td className="py-3 pr-4 text-xs"><span className={Boolean(regulation.partner_required_info_received) ? 'text-success' : 'text-muted-foreground'}>{regulation.partner_required_info_received ? 'Вся информация получена' : 'Информация ожидается'}</span></td>
                            <td className="py-3 pr-4 text-xs font-medium">{TERMINATION_PATH_LABELS[row.legal_termination_path] || 'Не выбран'}</td>
                            <td className="py-3 pr-4 text-xs"><div>Подготовлено: {regulationDate(regulation, 'legal_termination_prepared_on') || '—'}</div><div className="mt-1">Отправлено: {regulationDate(regulation, 'legal_termination_sent_on') || regulationDate(regulation, 'legal_unilateral_sent_on') || '—'}</div></td>
                            <td className="py-3 pr-4 text-xs"><div className={blockDueOverdue('IN_PROGRESS', regulationDate(regulation, 'legal_sign_due_on')) ? 'text-error' : 'text-muted-foreground'}>До: {regulationDate(regulation, 'legal_sign_due_on') || 'не указан'}</div><div className="mt-1 text-muted-foreground">{row.termination_signed ? `Подписано: ${row.termination_signed_on || 'дата не указана'}` : 'Не подписано'}</div></td>
                          </>
                        )}
                        {activeFormStep === 'technical' && (
                          <>
                            <td className="py-3 pr-4 text-xs"><span className={countTechClosureItems((regulation.technical_checklist ?? {}) as Record<string, boolean>) === TECH_CLOSURE_ITEMS.length ? 'text-success' : 'text-muted-foreground'}>Выполнено {countTechClosureItems((regulation.technical_checklist ?? {}) as Record<string, boolean>)}/{TECH_CLOSURE_ITEMS.length}</span></td>
                            <td className="py-3 pr-4 text-xs"><span className={regulationDate(regulation, 'tech_legal_notified_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'tech_legal_notified_on') || 'Не сообщено'}</span></td>
                            <td className="py-3 pr-4 text-xs"><span className={regulationDate(regulation, 'status_closed_information_sent_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'status_closed_information_sent_on') || 'Не передано'}</span></td>
                            <td className="py-3 pr-4 text-xs"><div className={regulationDate(regulation, 'status_closed_information_sent_on') ? 'text-success' : 'text-muted-foreground'}>{regulationDate(regulation, 'status_closed_information_sent_on') ? 'Передано' : (row.termination_signed || regulationDate(regulation, 'legal_unilateral_sent_on') ? 'Можно передавать в «Закрыта»' : 'Ждём юридический триггер')}</div></td>
                          </>
                        )}
                        <td className="py-3 pr-3 text-right">
                          <div className="flex justify-end gap-2 whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (activeFormStep === 'departments') {
                                  openDepartmentsModal(row)
                                  return
                                }
                                fillFromRow(row)
                                if (
                                  (activeFormStep === 'termination' || activeFormStep === 'technical')
                                  && regulationDetailsByStudio[row.studio_number]?.case_scenario_status === 'CLOSURE_CONFIRMED'
                                ) setActiveFormStep(activeFormStep)
                              }}
                              title={activeFormStep === 'departments' ? 'Открыть задачи отделов' : 'Открыть кейс'}
                            >
                              <Pencil size={16} aria-hidden="true" />
                              <span className="sr-only">{activeFormStep === 'departments' ? 'Открыть задачи отделов' : 'Открыть кейс'}</span>
                            </Button>
                            {isEmergencyCase && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="!border-error !text-error hover:!bg-error/10"
                                onClick={() => openEmergencyClosureForRow(row)}
                                disabled={saving}
                                title="Открыть модалку экстренного кейса"
                              >
                                <AlertTriangle size={16} aria-hidden="true" />
                                <span className="sr-only">Открыть экстренный кейс</span>
                              </Button>
                            )}
                            {false && activeFormStep === 'technical' && (
                              <Button
                                size="sm"
                                onClick={() => void completePreparation(row)}
                              disabled={!canComplete || endingId === row.id}
                              title={canComplete
                                ? 'Окончательное закрытие: все отметки собраны, студия закрыта фактически'
                                : `Окончательное закрытие недоступно: не хватает — ${missing.join('; ')}`}
                            >
                              <CheckCircle2 size={16} aria-hidden="true" />
                              <span className="sr-only">Окончательное закрытие</span>
                            </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openCancelDialog(row)}
                              disabled={endingId === row.id}
                              title="Перенести в архив (нужна причина)"
                            >
                              <Trash2 size={16} aria-hidden="true" />
                              <span className="sr-only">Перенести в архив</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <Card className="space-y-4">
        <button
          type="button"
          onClick={() => setInactiveOpen(open => !open)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <div>
            <h2 className="text-base font-semibold">Неактивные записи</h2>
            <div className="mt-1 text-xs text-muted-foreground">
              Записи, у которых статус подготовки к закрытию уже снят.
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            <span>{filteredInactiveRows.length}</span>
            <ChevronDown
              size={18}
              className={`transition-transform ${inactiveOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </div>
        </button>

        {inactiveOpen && (
          loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Загрузка...</div>
        ) : filteredInactiveRows.length === 0 ? (
          <div className="rounded-md border border-border py-8 text-center text-sm text-muted-foreground">
            Неактивных записей не найдено.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1140px] w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Студия</th>
                  <th className="py-2 pr-3 font-medium">Статус</th>
                  <th className="py-2 pr-3 font-medium">Исход</th>
                  <th className="py-2 pr-3 font-medium">Введён</th>
                  <th className="py-2 pr-3 font-medium">Снят</th>
                  <th className="py-2 pr-3 font-medium">Добавил</th>
                  <th className="py-2 pr-3 font-medium">Ожидали</th>
                  <th className="py-2 pr-3 font-medium">Уведомление</th>
                  <th className="py-2 pr-3 font-medium">После закрытия</th>
                  <th className="py-2 font-medium">Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {filteredInactiveRows.map(row => {
                  const days = daysUntil(row.expected_closure_on)
                  return (
                    <tr key={row.id} className="border-b border-border/70 align-top">
                      <td className="py-3 pr-3">
                        <div className="max-w-[260px] text-left font-medium text-foreground">
                          {row.city_name && (
                            <span>{row.city_name}</span>
                          )}
                          {row.city_name && <span className="mx-1 text-muted-foreground">·</span>}
                          <span className="text-primary">#{row.studio_number}</span>
                          {row.address && (
                            <span className="ml-1 font-normal text-muted-foreground">· {row.address}</span>
                          )}
                        </div>
                        {formatOwnerLabel(row) && (
                          <div className="mt-0.5 flex max-w-[240px] items-center gap-1 text-xs text-muted-foreground">
                            <UserRound size={12} />
                            <span className="truncate">{formatOwnerLabel(row)}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <Badge variant="neutral" className="text-xs">{row.status_code ?? '—'}</Badge>
                      </td>
                      <td className="py-3 pr-3">
                        <Badge
                          variant={row.end_kind === 'COMPLETED' ? 'success' : row.end_kind === 'CANCELLED' ? 'warning' : 'neutral'}
                          className="text-xs"
                        >
                          {row.end_kind === 'COMPLETED' ? 'Закрыта' : row.end_kind === 'CANCELLED' ? 'Отменена' : '—'}
                        </Badge>
                        {row.end_reason && (
                          <div className="mt-1 max-w-[200px] whitespace-pre-wrap text-xs text-muted-foreground">
                            {row.end_reason}
                          </div>
                        )}
                        {row.ended_by_name && (
                          <div className="mt-0.5 max-w-[200px] truncate text-xs text-muted-foreground">
                            {row.ended_by_name}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap">{row.preparation_started_on}</td>
                      <td className="py-3 pr-3 whitespace-nowrap">{row.preparation_ended_on ?? '—'}</td>
                      <td className="py-3 pr-3">
                        <div className="max-w-[150px] truncate text-xs text-muted-foreground">
                          {row.created_by_name?.trim() || '—'}
                        </div>
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap">
                        {row.expected_closure_on ? (
                          <div>
                            <div>{row.expected_closure_on}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {days === null ? '—' : days < 0 ? `${Math.abs(days)} дн. назад` : `${days} дн.`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Не указано</span>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        {row.owner_official_notice_received ? (
                          <div className="text-success">
                            <div className="flex items-center gap-1">
                              <CheckCircle2 size={14} />
                              Получено
                            </div>
                            {row.owner_official_notice_received_on && (
                              <div className="mt-0.5 text-xs text-muted-foreground">{row.owner_official_notice_received_on}</div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <XCircle size={14} />
                            Нет
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <div className="space-y-1 text-xs">
                          <div className={row.closure_confirmation_received ? 'text-success' : 'text-muted-foreground'}>
                            <span className="inline-flex items-center gap-1">
                              {row.closure_confirmation_received ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                              Подтверждение
                            </span>
                            {row.closure_confirmation_received_on && (
                              <span className="ml-1 text-muted-foreground">{row.closure_confirmation_received_on}</span>
                            )}
                          </div>
                          <div className={row.termination_signed ? 'text-success' : 'text-muted-foreground'}>
                            <span className="inline-flex items-center gap-1">
                              {row.termination_signed ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                              Расторжение
                            </span>
                            {row.termination_signed_on && (
                              <span className="ml-1 text-muted-foreground">{row.termination_signed_on}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="max-w-[220px] whitespace-pre-wrap text-xs text-muted-foreground">
                          {row.note || '—'}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
        )}
      </Card>

      {emergencyModalOpen && currentCaseStudio && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !saving && setEmergencyModalOpen(false)}
        >
          <div
            className="max-h-[calc(100dvh-2rem)] w-full max-w-2xl overflow-y-auto rounded-xl border border-error/50 bg-background p-5 shadow-xl"
            onClick={event => event.stopPropagation()}
            onChange={() => setFormDirty(true)}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 text-base font-semibold text-error">
                  <AlertTriangle size={18} />
                  Экстренное закрытие
                </h3>
                {emergencyIntroVisible && <p className="mt-2 text-sm font-medium">
                  При экстренном закрытии кейс будет доступен на этапах «Расторжение» и «Закрытие и база».
                </p>}
                {emergencyIntroVisible && <p className="mt-1 text-xs text-muted-foreground">
                  Заполните ключевые данные сейчас. После сохранения ответственные смогут параллельно заполнять оба этапа в обычной модалке.
                </p>}
              </div>
              <button type="button" onClick={() => setEmergencyModalOpen(false)} disabled={saving} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Закрыть">
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block space-y-1 md:col-span-2">
                <span className="text-sm font-medium">Причина экстренного закрытия</span>
                <textarea value={closureReason} onChange={event => setClosureReason(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Что произошло и почему кейс нужно ускорить" />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Дата экстренного закрытия</span>
                <input type="date" value={caseScenarioChangedOn} onChange={event => setCaseScenarioChangedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Клиентская кредиторка, ₽</span>
                <input type="text" inputMode="decimal" value={clientLiabilityAmount} onChange={event => setClientLiabilityAmount(event.target.value)} placeholder="0" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Прочие долги, ₽</span>
                <input type="text" inputMode="decimal" value={otherDebtAmount} onChange={event => setOtherDebtAmount(event.target.value)} placeholder="0" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Маршрут расторжения</span>
                <select value={legalTerminationPath} onChange={event => setLegalTerminationPath(event.target.value as TerminationPath)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  {TERMINATION_PATH_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="block space-y-1 md:col-span-2">
                <span className="text-sm font-medium">Долги и комментарий по кредиторке</span>
                <textarea value={otherDebtNote} onChange={event => setOtherDebtNote(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Кому должны, сроки, что уже проверено" />
              </label>
              <label className="block space-y-1 md:col-span-2">
                <span className="text-sm font-medium">Юридические риски</span>
                <textarea value={legalBlockNote} onChange={event => setLegalBlockNote(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Что важно учесть юристам" />
              </label>
              <label className="block space-y-1 md:col-span-2">
                <span className="text-sm font-medium">Комментарий</span>
                <textarea value={caseScenarioNote} onChange={event => setCaseScenarioNote(event.target.value)} rows={2} className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" placeholder="Дополнительный контекст для коллег" />
              </label>
            </div>

            <div className="mt-5 flex items-center justify-between gap-2 border-t border-border pt-4">
              <Button type="button" size="sm" variant="outline" onClick={() => setEmergencyModalOpen(false)} disabled={saving}>Отмена</Button>
              <Button
                type="button"
                size="sm"
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap !bg-error !text-white hover:!bg-error/90"
                onClick={() => {
                  emergencySaveRef.current = true
                  void save('termination', {
                    status: 'CLOSURE_CONFIRMED',
                    changedOn: caseScenarioChangedOn || todayIso(),
                    note: caseScenarioNote.trim() || 'Запущено экстренное закрытие: кейс доступен на этапах расторжения и технического закрытия.',
                  })
                }}
                disabled={saving}
              >
                <CheckCircle2 size={16} />
                {saving ? 'Сохраняю...' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {statusHandoffTarget && selectedActiveRow && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => !statusHandoffSubmitting && setStatusHandoffTarget(null)}>
          <div className="w-full max-w-lg rounded-md border border-border bg-card p-4 shadow-xl" onClick={event => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold">Передать информацию</h3>
                <p className="mt-1 text-sm text-muted-foreground">Будет создан кейс «{statusHandoffTarget === 'NO_SALES' ? 'Студия без продаж' : 'Студия закрыта'}» для студии #{selectedActiveRow.studio_number} в разделе «Реестр студий». Статус студии здесь не изменяется.</p>
              </div>
              <button type="button" onClick={() => setStatusHandoffTarget(null)} disabled={statusHandoffSubmitting} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted" title="Закрыть" aria-label="Закрыть"><X size={16} /></button>
            </div>
            {statusHandoffTarget === 'CLOSED' && <div className="mt-4 grid gap-3 sm:grid-cols-2"><label className="block space-y-1"><span className="text-sm font-medium">Дата фактического закрытия студии</span><input type="date" required value={statusHandoffClosureOn} disabled={Boolean(selectedActiveRow.expected_closure_on)} onChange={event => setStatusHandoffClosureOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-60" /><span className="text-xs text-muted-foreground">Эта дата попадёт в запись о закрытии; дата передачи фиксируется отдельно автоматически.</span></label><label className="block space-y-1"><span className="text-sm font-medium">Тип закрытия</span><select value={statusHandoffClosureType} onChange={event => setStatusHandoffClosureType(event.target.value as StudioClosureType)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"><option value="STANDARD">Обычное закрытие</option><option value="REBRANDING">Ребрендинг</option><option value="UNAPPROVED">Не согласовано</option></select></label></div>}
            <div className="mt-4 rounded-md border border-border/70 bg-muted/20 p-3"><div className="text-sm font-medium">Коротко о закрытии</div><p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{buildClosureHandoffSummary(selectedActiveRow, statusHandoffTarget, statusHandoffClosureOn, statusHandoffClosureType).replace(/^Коротко о закрытии:\n/, '')}</p></div>
            <label className="mt-4 block space-y-1"><span className="text-sm font-medium">Комментарий для получателя</span><textarea value={statusHandoffComment} onChange={event => setStatusHandoffComment(event.target.value)} rows={3} placeholder="Дополнительный контекст, если нужен" className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" /></label>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setStatusHandoffTarget(null)} disabled={statusHandoffSubmitting}>Отмена</Button>
              <Button type="button" size="sm" className="inline-flex items-center justify-center whitespace-nowrap" onClick={() => void sendStatusHandoffInformation()} disabled={statusHandoffSubmitting || (statusHandoffTarget === 'CLOSED' && !statusHandoffClosureOn)}>{statusHandoffSubmitting ? 'Передаю...' : 'Передать информацию'}</Button>
            </div>
          </div>
        </div>
      )}

      {departmentModalRow && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onClick={requestCloseDepartmentModal}
        >
          <div
            className="my-8 w-full max-w-5xl overflow-hidden rounded-xl bg-background shadow-lg"
            onClick={event => event.stopPropagation()}
            onChange={() => setFormDirty(true)}
            ref={departmentModalContentRef}
          >
            <div className="flex items-start justify-between gap-3 border-b border-border p-4">
              <div>
                <h3 className="text-base font-semibold">Задачи отделов по студии #{departmentModalRow.studio_number}</h3>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatStudioMeta(departmentModalRow) || 'Данные по студии не найдены'}
                </div>
              </div>
              <button
                type="button"
                onClick={requestCloseDepartmentModal}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Закрыть"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid min-h-[520px] md:grid-cols-[240px_1fr]">
              <div className="border-b border-border bg-muted/30 p-3 md:border-b-0 md:border-r">
                <div className="mb-2 px-2 text-xs font-medium text-muted-foreground">Отделы</div>
                <div className="grid gap-1">
                  {DEPARTMENT_BLOCKS.map(block => {
                    const active = block.id === activeDepartmentBlock
                    return (
                      <button
                        key={block.id}
                        type="button"
                        onClick={() => setActiveDepartmentBlock(block.id)}
                        className={`rounded-md px-3 py-2 text-left text-sm transition-colors ${
                          active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-background hover:text-foreground'
                        }`}
                      >
                        {block.title}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-4 p-4">
                <div>
                  <h4 className="text-sm font-semibold">
                    {DEPARTMENT_BLOCKS.find(block => block.id === activeDepartmentBlock)?.title}
                  </h4>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Эти поля сохраняются в выбранный кейс, а не во все строки.
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Статус</span>
                    <select
                      value={activeDepartmentFields.status}
                      onChange={event => activeDepartmentFields.setStatus(event.target.value as WorkBlockStatus)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      {BLOCK_STATUS_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Срок</span>
                    <input
                      type="date"
                      value={activeDepartmentFields.dueOn}
                      onChange={event => activeDepartmentFields.setDueOn(event.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Исполнитель</span>
                    <input
                      type="text"
                      value={activeDepartmentFields.assignee}
                      onChange={event => activeDepartmentFields.setAssignee(event.target.value)}
                      placeholder="Кто отвечает"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <label className="block space-y-1">
                  <span className="text-sm font-medium">Результат / что ждём</span>
                  <textarea
                    value={activeDepartmentFields.note}
                    onChange={event => activeDepartmentFields.setNote(event.target.value)}
                    rows={5}
                    placeholder="Что проверено, что сделано, что осталось получить"
                    className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>

                {activeDepartmentBlock === 'legal' && (
                  <div className="rounded-md border border-border p-3">
                    <h4 className="text-sm font-semibold">Юридический маршрут</h4>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="block space-y-1">
                        <span className="text-sm font-medium">Регламент отправлен</span>
                        <input type="date" value={legalRegulationSentOn} onChange={event => setLegalRegulationSentOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                      </label>
                      <label className="block space-y-1">
                        <span className="text-sm font-medium">Документы до</span>
                        <input type="date" value={legalDocumentsDueOn} onChange={event => setLegalDocumentsDueOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                      </label>
                    </div>
                    <label className="mt-3 block space-y-1">
                      <span className="text-sm font-medium">Маршрут расторжения</span>
                      <select value={legalTerminationPath} onChange={event => setLegalTerminationPath(event.target.value as TerminationPath)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                        {TERMINATION_PATH_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                {activeDepartmentBlock === 'finance' && (
                  <div className="rounded-md border border-border p-3">
                    <h4 className="text-sm font-semibold">Последний расчёт</h4>
                    {currentCaseStudio && (
                      <div className="mt-2 rounded-md bg-muted/30 px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground">Текущий долг по роялти из базы</div>
                        <div className="mt-0.5 font-medium">{formatRub(currentCaseStudio.royalty_debt_rub)}</div>
                      </div>
                    )}
                    <div className="mt-3 grid gap-2 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={financeContractsChecked} onChange={event => setFinanceContractsChecked(event.target.checked)} className="h-4 w-4" />Договоры и сроки проверены</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={financeRevenueChecked} onChange={event => setFinanceRevenueChecked(event.target.checked)} className="h-4 w-4" />Выручка сверена и зафиксирована</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={financeRoyaltyAccrued} onChange={event => setFinanceRoyaltyAccrued(event.target.checked)} className="h-4 w-4" />Последний роялти начислен</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={financeDebtFixed} onChange={event => setFinanceDebtFixed(event.target.checked)} className="h-4 w-4" />Итоговая задолженность зафиксирована</label>
                    </div>
                    <label className="mt-3 block space-y-1">
                      <span className="text-sm font-medium">Ждём поздние платежи до</span>
                      <input type="date" value={financeLatePaymentsWaitUntil} onChange={event => setFinanceLatePaymentsWaitUntil(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                  </div>
                )}

                {activeDepartmentBlock === 'tech' && (
                  <div className="rounded-md border border-border p-3">
                    <h4 className="text-sm font-semibold">Техническая подготовка</h4>
                    <div className="mt-3 grid gap-2 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={techLiabilityPrecheckDone} onChange={event => setTechLiabilityPrecheckDone(event.target.checked)} className="h-4 w-4" />Клиенты и кредиторка предварительно проверены</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={techSystemsTransferReady} onChange={event => setTechSystemsTransferReady(event.target.checked)} className="h-4 w-4" />Системы подготовлены к закрытию/переносу</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={techListokHideRequested} onChange={event => setTechListokHideRequested(event.target.checked)} className="h-4 w-4" />Заявка на скрытие филиала в листке подана</label>
                    </div>
                    <label className="mt-3 block space-y-1">
                      <span className="text-sm font-medium">Доступы закрыты</span>
                      <input type="date" value={techAccessesClosedOn} onChange={event => setTechAccessesClosedOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                  </div>
                )}

                {activeDepartmentBlock === 'pedagogy' && (
                  <div className="rounded-md border border-border p-3">
                    <h4 className="text-sm font-semibold">Педагоги и Академия</h4>
                    <div className="mt-3 grid gap-2 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={pedagogySecretShoppersStopped} onChange={event => setPedagogySecretShoppersStopped(event.target.checked)} className="h-4 w-4" />Тайные покупатели по студии/городу остановлены</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={pedagogyPlansAdjusted} onChange={event => setPedagogyPlansAdjusted(event.target.checked)} className="h-4 w-4" />Планы отсмотров скорректированы</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={pedagogyAccessesClosed} onChange={event => setPedagogyAccessesClosed(event.target.checked)} className="h-4 w-4" />Доступы Академии закрыты/переданы</label>
                    </div>
                  </div>
                )}

                {activeDepartmentBlock === 'sales' && (
                  <div className="rounded-md border border-border p-3">
                    <h4 className="text-sm font-semibold">Продажа и открытия</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="block space-y-1">
                        <span className="text-sm font-medium">Цена продажи</span>
                        <input type="text" inputMode="decimal" value={salesPriceAmount} onChange={event => setSalesPriceAmount(event.target.value)} placeholder="0" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                      </label>
                      <label className="block space-y-1">
                        <span className="text-sm font-medium">Передача до</span>
                        <input type="date" value={salesTransferDueOn} onChange={event => setSalesTransferDueOn(event.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                      </label>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={salesHasBuyer} onChange={event => setSalesHasBuyer(event.target.checked)} className="h-4 w-4" />Покупатель есть</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={salesOpeningsHandoffDone} onChange={event => setSalesOpeningsHandoffDone(event.target.checked)} className="h-4 w-4" />Передача в отдел открытий сделана</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={salesBuyerDkkSynced} onChange={event => setSalesBuyerDkkSynced(event.target.checked)} className="h-4 w-4" />ДКК покупателя синхронизирован</label>
                    </div>
                    <label className="mt-3 block space-y-1">
                      <span className="text-sm font-medium">Покупатель / статус сделки</span>
                      <textarea value={salesBuyerSummary} onChange={event => setSalesBuyerSummary(event.target.value)} rows={2} placeholder="Кто покупает, статус, что нужно подготовить" className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm" />
                    </label>
                  </div>
                )}

                {warningPanel}

                {emergencyClosureAction}

                <div className="grid gap-3 border-t border-border pt-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-error"
                    onClick={() => openCancelDialog(departmentModalRow)}
                    disabled={saving}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Перенести в архив
                  </Button>
                  <div className="flex min-w-0 flex-wrap justify-start gap-2 md:justify-end">
                    <Button size="sm" variant="outline" onClick={requestCloseDepartmentModal} disabled={saving}>
                      Закрыть
                    </Button>
                    <Button size="sm" className="inline-flex items-center justify-center gap-1 whitespace-nowrap" onClick={() => void save()} disabled={saving || loading}>
                      <CheckCircle2 size={16} className="mr-1" />
                      {saving ? 'Сохраняю...' : 'Сохранить задачи'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onClick={() => setCancelTarget(null)}
        >
          <div
            className="my-8 w-full max-w-lg space-y-3 rounded-xl bg-background p-4 shadow-lg"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                <Trash2 size={16} className="text-primary" />
                Перенести в архив
              </h3>
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Закрыть"
              >
                <X size={16} />
              </button>
            </div>

            <div className="rounded-md border border-border p-3 text-sm">
              <div className="font-medium">
                {cancelTarget.city_name && <span>{cancelTarget.city_name} · </span>}
                <span className="text-primary">#{cancelTarget.studio_number}</span>
              </div>
              {formatOwnerLabel(cancelTarget) && (
                <div className="mt-0.5 text-xs text-muted-foreground">{formatOwnerLabel(cancelTarget)}</div>
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              Кейс будет деактивирован и перемещён в неактивные записи. Данные не удаляются, а студия останется
              в своём фактическом статусе ({cancelTarget.status_code ?? 'не указан'}).
            </div>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Причина переноса в архив</span>
              <textarea
                value={cancelReason}
                onChange={event => setCancelReason(event.target.value)}
                rows={3}
                autoFocus
                placeholder="Например: владелец передумал закрываться; запись заведена по ошибке"
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            {error && (
              <div className="rounded-md border border-error/40 bg-error/10 p-2 text-xs text-error">{error}</div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                className="inline-flex items-center gap-2 text-error"
                onClick={() => void cancelPreparation()}
                disabled={cancelReason.trim().length < 3 || endingId === cancelTarget.id}
              >
                <Trash2 size={16} aria-hidden="true" />
                {endingId === cancelTarget.id ? 'Переношу...' : 'Перенести в архив'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {unsavedCloseTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-background p-5 shadow-xl">
            <h3 className="text-base font-semibold">Есть несохранённые изменения</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Сохранить изменения перед закрытием модалки?
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setUnsavedCloseTarget(null)}>
                Отмена
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={discardUnsavedChangesAndClose}>
                Выйти без сохранения
              </Button>
              <Button type="button" size="sm" onClick={saveAndCloseWithUnsavedChanges} disabled={saving}>
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
